import { isDevMode, InjectionToken, LOCALE_ID, ɵɵdefineNgModule, ɵɵdefineInjector, ɵɵinject, ɵsetClassMetadata, NgModule, CUSTOM_ELEMENTS_SCHEMA, Optional, SkipSelf, NgModuleFactory, Injector, Compiler, ComponentFactoryResolver, Inject, ɵɵdefineInjectable, Injectable, ɵɵdirectiveInject, ɵɵdefinePipe, Pipe, ɵɵsetNgModuleScope, SecurityContext, ElementRef, ɵɵdefineDirective, Directive, Input, ɵɵattribute, ɵɵProvidersFeature, forwardRef, ɵɵInheritDefinitionFeature, ɵɵNgOnChangesFeature, ɵɵgetInheritedFactory } from '@angular/core';
import { kebabToCamelCase, coerceNumberProperty, isValidSwedishEmailAddress, isValidFinnishEmailAddress, isValidSwedishBusinessNumber, isValidFinnishBusinessNumber, isValidSwedishPersonalNumber, isValidAgeRange, isValidFinnishPersonalNumber, isValidSwedishPhoneNumber, isValidFinnishPhoneNumber, isValidSwedishPremiseId, isValidInternationalPostalCode, isValidSwedishPostalCode, isValidFinnishPostalCode, isValidSwedishApartmentNumber, isValidSwedishFinnishPostalTown, isValidPassword, isValidGridAreaId, isValidPersonalName, isValidStreetName, isValidCareOfAddress } from '@vattenfall/util';
import { Subject, of, throwError, ReplaySubject, Observable, fromEvent, merge } from 'rxjs';
import { distinctUntilChanged, takeUntil, delay, filter, map, startWith } from 'rxjs/operators';
import { __awaiter } from 'tslib';
import { registerLocaleData, DOCUMENT, DatePipe, DecimalPipe, CommonModule } from '@angular/common';
import { createCustomElement } from '@angular/elements';
import { ElementZoneStrategyFactory } from 'elements-zone-strategy';
import { TestComponentRenderer, TestBed } from '@angular/core/testing';
import { Store, Actions } from '@ngxs/store';
import { HTTP_INTERCEPTORS, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { pickAll } from 'ramda';
import { DomSanitizer } from '@angular/platform-browser';
import { Validators, NG_VALIDATORS } from '@angular/forms';

/**
 * `ViewState` is a mixin function which adds NgXS state management utilities to an Angular component.
 *
 * When used, it can unwrap state properties and define them as immutable properties to a component class, dispatch actions and add a HTML select list to the component view that allows you to inspect the component in various view states.
 *
 * **Example**
 *
 * ```
 * @Component({ ... })
 * ExampleComponent extends ViewState(ExampleViewStateModel) {
 *   constructor(store: Store, elementRef: ElementRef, renderer: Renderer2) {
 *     super()
 *   }
 * }`
 * ```
 * @param ViewStateModel NgXS view state model class
 */
function ViewState(ViewStateModel) {
    /**
     * Whether the application is running in an emulated test environment or not
     */
    const isTest = navigator.userAgent.includes("Node.js") || navigator.userAgent.includes("jsdom");
    /**
     * Whether the application is running in an production setting or not
     */
    const isProduction = !isDevMode() && !window.parent.hasOwnProperty("epi");
    return class ViewState extends ViewStateModel {
        constructor() {
            super(...arguments);
            this.destroyed$ = new Subject();
        }
        /**
         * Sets a component's view state
         */
        set viewState(viewState) {
            this.store.dispatch(this.viewStates[viewState]);
        }
        viewStateInit(properties, dependencies, viewStates) {
            try {
                this.store = dependencies.store;
                this.elementRef = dependencies.elementRef;
                this.renderer = dependencies.renderer;
                this.handleProperties(Object.assign(properties, this.handleInputs()));
                if (viewStates) {
                    this.viewStates = viewStates;
                    this.handleSelect(dependencies);
                }
            }
            catch (error) {
                console.error(error);
            }
        }
        /**
         * Go through each input provided to this class and extract all inputs with a `$state` reference value into a `Properties` object.
         */
        handleInputs() {
            return Object.entries(this).reduce((properties, [key, path]) => path && typeof path === "string" && path.startsWith("$state:")
                ? Object.assign(properties, {
                    [path.replace("$state:", "")]: kebabToCamelCase(key),
                })
                : properties, {});
        }
        /**
         * Go through provided properties and, subscribes to state changes, defines properties in component class or invokes property callbacks
         */
        handleProperties(properties) {
            const selectStateValueFromPath = (path) => (state) => path.split(".").reduce((o = {}, key) => o[key], state);
            const selectPropKeyFromPath = (path) => path.split(".").pop();
            Object.entries(properties).forEach(([path, handler]) => {
                this.store
                    // Select state property from path
                    .select(selectStateValueFromPath(path))
                    .pipe(
                // Only emit when state values has changed
                distinctUntilChanged(), 
                // Continue to emit until component has been destroyed
                takeUntil(this.destroyed$))
                    .subscribe((value) => {
                    switch (typeof handler) {
                        // The handler is an input
                        case "string":
                            Object.defineProperty(this, handler, {
                                value: value,
                                writable: true,
                            });
                            break;
                        // The handler is a callback function
                        case "function":
                            handler(value);
                            break;
                        // The handler is a state reference
                        default:
                            Object.defineProperty(this, selectPropKeyFromPath(path), {
                                value: value,
                                writable: true,
                            });
                    }
                });
            });
        }
        /**
         * Generates a HTML select list in component view when in devmode
         */
        handleSelect(dependencies) {
            // We really need a better solution for the drop down going forwards
            if (!isProduction && !isTest) {
                // Create a dropdown of all view states and attach it to the host element
                const host = dependencies.elementRef.nativeElement;
                const select = dependencies.renderer.createElement("select");
                // Append an empty option
                select.appendChild(dependencies.renderer.createElement("option"));
                for (const value of Object.keys(this.viewStates)) {
                    const option = dependencies.renderer.createElement("option");
                    option.innerText = value;
                    option.value = value;
                    select.appendChild(option);
                }
                select.style.display = "none";
                select.style.position = "absolute";
                select.style.zIndex = "999";
                // Change viewstate when a new value has been selected
                select.addEventListener("change", (event) => {
                    // removes the empty option as soon as a value has been selected
                    if (event.target.value) {
                        for (const option of Array.from(select.children))
                            if (!option.value)
                                option.remove();
                    }
                    if (event.target)
                        this.viewState = event.target.value;
                });
                // Toggle visability on hover
                host.addEventListener("mouseenter", () => {
                    select.style.display = "block";
                });
                host.addEventListener("mouseleave", () => {
                    select.style.display = "none";
                });
                // Add select list in view
                if (host.shadowRoot)
                    host.shadowRoot.prepend(select);
            }
        }
        /**
         * Taps into component ngOnDestroy lifecycle hook so that all state subscriptions are resolved on component destruction
         */
        ngOnDestroy() {
            this.destroyed$.next();
            this.destroyed$.complete();
        }
    };
}

/**
 * `Environment` configuration object's injection token.
 *
 * Used for injecting the environment configuration object in a component, directive, service or class.
 *
 * The following example injects the environment token in a class.
 *
 * ```typescript
 * class Example {
 *  constructor(@Optional() @Inject(ENVIRONMENT) private environment: Environment) {}
 * }
 * ```
 */
const ENVIRONMENT = new InjectionToken("environment");
/**
 * List of locales supported by this application
 */
const ENVIRONMENT_LOCALES = ["sv-SE", "sv-FI", "fi-FI"];
/**
 * List of regions supported by this application
 */
const ENVIRONMENT_REGIONS = ["se", "fi"];
/**
 * List of languages supported by this application
 */
const ENVIRONMENT_LANGUAGES = ["sv", "fi"];
/**
 * The expected errors that could happen as a result of using this package
 */
var Errors;
(function (Errors) {
    Errors["Undefined"] = "No application configuration object was passed to the constructor. To solve this, make sure that the Vattenfall class is instantiated with proper runtime configurations.";
    Errors["Locale"] = "The provided locale is either empty or not supported. Please use one of the supported locales: 'sv-SE', 'sv-FI' or 'fi-FI'.";
    Errors["BundleURL"] = "The provided bundle URL is empty.";
    Errors["BundleVersion"] = "The bundle version either is empty.";
    Errors["Initialized"] = "Application already initialized. There should only be one instance of Vattenfall Elements, please make sure the it is not called more than once.";
    Errors["Exception"] = "Vattenfall Elements was not bootstraped. Failed with exception.";
    Errors["StaticProvidersCallback"] = "No static providers callback. Make sure that a platform browser dynamic callback has been passed in 'main.ts' when calling 'createElementsClass'.";
    Errors["ModuleImported"] = "Could not import 'ElementsModule'. This module has already been imported, please sure it is imported once and in the application root module.";
    Errors["ElementDefined"] = "This custom element has already been defined. This suggest that 1) there might be another custom element with the same name existing on this site, or 2) the 'customElements.define' method might be called twice for the same element. Custom elements should be defined using the 'ElementsModule'.";
    Errors["BootstrapElement"] = "No bootstraping element. Make sure that a custom element has been defined in the module's bootstrap array.";
    Errors["LoadModule"] = "Could not import module from dynamic path. Make sure that the custom elements module has been properly declared in the ElementsModule using dynamic import syntax.";
    Errors["NoModule"] = "Could not load module. Make sure that a module has been declared either as a dynamic import using the 'loadChildren' property or as a static import using the 'elementModule' property.";
    Errors["CompileModule"] = "Could not compile module. Make sure that all libraries are built using the same view engine. Re-build all applications and libraries in the workspace and try again.";
    Errors["ElementNotFound"] = "Could not compile module. No element was found with this tag.";
    Errors["CustomElement"] = "Could not create custom element from component.";
})(Errors || (Errors = {}));
/**
 * Global application details, such as whether the application has been initalized or not and the path to the app module.
 */
const app = {
    livemode: true,
    initialized: false,
};
/**
 * Sets the global application bootstrap configurations and returns the `Elements` class.
 * @param onStaticProvidersResolved The callback function to be invoked with the static provides from the Elements class
 */
function createElementsClass(onStaticProvidersResolved, livemode = true) {
    app.onStaticProvidersResolved = onStaticProvidersResolved;
    app.livemode = livemode;
    return Elements;
}
/**
 * Resets the global application bootstrap configurations and returns the `Elements` class.
 * @param onStaticProvidersResolved The callback function to be invoked with the static provides from the Elements class
 */
function resetElementsClass(onStaticProvidersResolved, livemode = true) {
    app.initialized = false;
    app.livemode = livemode;
    app.onStaticProvidersResolved = onStaticProvidersResolved;
    return Elements;
}
/**
 * `Elements` is the class responsible for bootstraping instances of a Vattenfall Elements application from an environment configuration object.
 *
 * The environment configuration object is a set of runtime instructions required to bootstrap the application, such as the user's prefered locale or properties consumed by external data tracking services.
 * Optionally, callback functions can be passed to the constructor which will be called if the application bootstraping was successfull or not.
 *
 * **Usage**
 *
 * ```html
 * <script type="module">
 *  // Creates a instance of an Vattenfall Elements application serving
 *  // Swedish speaking users on localhost
 *  new Elements({
 *    livemode: false,
 *    locale: "sv-SE",
 *    trackingId: "UA-4663340-1",
 *    viewId: "180753764"
 *  })
 * </script>
 * ```
 */
class Elements {
    /**
     * @param config The application bootstrap `Config` object
     * @param onSuccess An optional callback function be called if the application bootstraping was successful
     * @param onError An optional callback function be called if the application bootstraping failed with exception
     */
    constructor(config, onSuccess, onError) {
        this.config = config;
        this.onSuccess = onSuccess;
        this.onError = onError;
        this.bootstrap(this.config)
            .then(() => {
            if (this.onSuccess)
                this.onSuccess();
            app.initialized = true;
        })
            .catch((error) => {
            if (this.onError)
                this.onError(error);
            console.error(Errors.Exception, error);
        });
    }
    /**
     * Performs a validity check of the provided environment object to make sure it contains all required properties and values. An invalid environment object will result in error.
     * @param environment the `Environment` configuration object
     */
    static validate(config) {
        if (!config || !Object.keys(config).length)
            throw new Error(Errors.Undefined);
        if (!config.locale || !ENVIRONMENT_LOCALES.includes(config.locale))
            throw new Error(Errors.Locale);
        if (!config.bundleUrl && app.livemode)
            throw new Error(Errors.BundleURL);
        if (!config.bundleVersion && app.livemode)
            throw new Error(Errors.BundleVersion);
        if (!app.onStaticProvidersResolved)
            throw new Error(Errors.StaticProvidersCallback);
        if (app.initialized)
            throw new Error(Errors.Initialized);
    }
    /**
     * Sets the base path from where static resources should be loaded, this is important when resources are loaded from an external endpoint like a CDN.
     * @param config The `Config` object
     */
    static publicPath(config) {
        if (app.livemode) {
            // The __webpack_public_path__ is a special configuration option that allows you to specify a base path
            // from where the application should load its static assets
            // @ts-ignore
            __webpack_public_path__ = `${config.bundleUrl}/${config.bundleVersion}/`;
        }
    }
    /**
     * Takes a locale configuration and lazy-loads its Angular locale data file from the relvant path.
     * @param locale
     */
    locale(locale) {
        switch (locale) {
            case "sv-SE":
                return import('@angular/common/locales/sv').then((module) => module.default);
            case "sv-FI":
                return import('@angular/common/locales/sv-FI').then((module) => module.default);
            case "fi-FI":
                return import('@angular/common/locales/fi').then((module) => module.default);
        }
    }
    /**
     * Returns the backend environment configurations from API
     * @param config
     */
    environment(config) {
        return __awaiter(this, void 0, void 0, function* () {
            if (app.livemode) {
                /**
                 * TODO (stefan): This implementation is not optimal and should be refactored.
                 *
                 * Everything that Vattenfall Elements needs in order to run should be declared as
                 * input parameters to the Elements class. Now we are getting this information from
                 * some backend that might or might not exist on that specific page.
                 *
                 * We need to update Episerver so that we are accessing the configs from there and
                 * provide them to the Elements class.
                 */
                return fetch("/api/elements/environment")
                    .then((response) => response.json())
                    .then(({ currentUserLocale, currentUserLanguage, acquisitionBaseUrl, selfServiceBaseUrl }) => ({
                    livemode: true,
                    locale: currentUserLocale,
                    language: currentUserLanguage.split("-")[0].toLowerCase(),
                    region: currentUserLocale.split("-")[1].toLowerCase(),
                    trackingId: config.trackingId,
                    viewId: config.viewId,
                    bundleUrl: config.bundleUrl,
                    bundleVersion: config.bundleVersion,
                    endpoints: {
                        // TODO (stefan): We should only use the base URL here since we might not alwayws end with '/elements' for example
                        selfServiceApi: `${selfServiceBaseUrl}/elements`,
                        acquisitionApi: `${acquisitionBaseUrl}/api/acquisition`,
                    },
                }));
            }
            else
                return (yield {
                    livemode: false,
                    locale: config.locale,
                    language: config.locale.split("-")[0].toLowerCase(),
                    region: config.locale.split("-")[1].toLowerCase(),
                    trackingId: config.trackingId,
                    viewId: config.viewId,
                    endpoints: {
                        selfServiceApi: "/elements",
                        acquisitionApi: "/api/acquisition",
                    },
                });
        });
    }
    /**
     * Tries to boostraps the application from the provided environment configuration object, and throws an error if unsuccessful.
     * @param config the `Config` configuration object
     */
    bootstrap(config) {
        return __awaiter(this, void 0, void 0, function* () {
            Elements.validate(config);
            Elements.publicPath(config);
            const environment = yield this.environment(config);
            const localeData = yield this.locale(config.locale);
            registerLocaleData(localeData, config.locale);
            if (app.onStaticProvidersResolved)
                return app.onStaticProvidersResolved([
                    {
                        provide: ENVIRONMENT,
                        useValue: environment,
                    },
                    // TODO (stefan): Temporarly providing the elements environment configs as a string based
                    // injection token. This is because we are referencing it from the @vattenfall/self-service library and
                    // which cannot import files from this directory.
                    // Onced we have the @vattenfall/core library setup this will no longer be an issue.
                    {
                        provide: "ELEMENTS_ENVIRONMENT",
                        useValue: environment,
                    },
                    {
                        provide: LOCALE_ID,
                        useValue: config.locale,
                    },
                ]);
        });
    }
}
/**
 * `ElementModules` arrays's injection token. Used for injecting the components array in the `ElementsModule`
 */
const ELEMENT_ROUTES = new InjectionToken("ELEMENT_ROUTES");
/**
 * `ElementsModule` manages the application `Element` components, and should be imported once at the application root.
 *
 * To create module use the `forRoot` static method, it will create a module that contain all the elements and instantiation logic required to load and generate elements.
 *
 * The module should be imported as follows: `imports: [ ElementsModule.forRoot(ELEMENT_ROUTES) ]` in your application's root module.
 */
class ElementsModule {
    constructor(parentModule) {
        // Make sure multiple instances of the environment module is not created
        // The elements module should only be imported once at the application root
        if (parentModule) {
            throw new Error(Errors.ModuleImported);
        }
    }
    static forRoot(elementRoutes) {
        return {
            ngModule: ElementsRootModule,
            providers: [
                {
                    provide: ELEMENT_ROUTES,
                    useValue: elementRoutes,
                },
            ],
        };
    }
}
ElementsModule.ɵmod = ɵɵdefineNgModule({ type: ElementsModule });
ElementsModule.ɵinj = ɵɵdefineInjector({ factory: function ElementsModule_Factory(t) { return new (t || ElementsModule)(ɵɵinject(ElementsModule, 12)); } });
/*@__PURE__*/ (function () { ɵsetClassMetadata(ElementsModule, [{
        type: NgModule,
        args: [{
                schemas: [CUSTOM_ELEMENTS_SCHEMA],
            }]
    }], function () { return [{ type: ElementsModule, decorators: [{
                type: Optional
            }, {
                type: SkipSelf
            }] }]; }, null); })();
/**
 * `ElementsRootModule` is the module return from the `ElementModule.forRoot` method. It is responsible for creating custom elements from a list of `ElementRoutes`.
 */
class ElementsRootModule {
    constructor(injector, compiler, componentFactoryResolver, elementRoutes, environment) {
        this.injector = injector;
        this.compiler = compiler;
        this.componentFactoryResolver = componentFactoryResolver;
        this.elementRoutes = elementRoutes;
        this.environment = environment;
        // The provided route is a proper element route
        for (const elementRoute of elementRoutes)
            if ("tagName" in elementRoute)
                if (document.body.querySelector(elementRoute.tagName))
                    this.compileElements(elementRoute);
                else
                    this.observeElement(elementRoute);
            // The provided route is a depreciated component class
            else {
                const tagName = this.componentFactoryResolver.resolveComponentFactory(elementRoute).selector;
                this.createElement(tagName, elementRoute);
            }
    }
    /**
     * Creates a custom element from an Angular component and register it in the browser custom elements registry
     * @param tagName Name for the new custom element. Note that custom element names must contain a hyphen.
     * @param component The Angular component to create a custom element from
     */
    createElement(tagName, component) {
        if (!customElements.get(tagName)) {
            try {
                const customElement = createCustomElement(component, {
                    injector: this.injector,
                    strategyFactory: new ElementZoneStrategyFactory(component, this.injector),
                });
                customElements.define(tagName, customElement);
            }
            catch (error) {
                throw new Error(`${Errors.CustomElement} Failed with exception: ${error.message || error}`);
            }
            return customElements.whenDefined(tagName);
        }
        else
            throw new Error(Errors.ElementDefined);
    }
    /**
     * Creates a mutation observer from `ElementRoute` that listens to inclusions of a specfic custom element tag in the DOM subtree and compiles components when found
     * @param elementRoute
     */
    observeElement(elementRoute) {
        const mutationObserver = new MutationObserver(() => __awaiter(this, void 0, void 0, function* () {
            if (document.body.querySelector(elementRoute.tagName)) {
                try {
                    yield this.compileElements(elementRoute);
                }
                finally {
                    // Make sure to disconnect on failed attempts to avoid an endless loop
                    mutationObserver.disconnect();
                }
            }
        }));
        mutationObserver.observe(document.body, {
            subtree: true,
            childList: true,
        });
    }
    /**
     * Compiles a elements from `ElementRoute` module and add class to custom element when compiling or failing
     * @param elementRoute The `ElementRoute` object
     */
    compileElements(elementRoute) {
        return __awaiter(this, void 0, void 0, function* () {
            const customElement = document.querySelector(elementRoute.tagName);
            if (!customElement)
                throw new Error();
            customElement.classList.add("compiling");
            try {
                // Add compiling class
                const elementModule = yield this.getElementModule(elementRoute);
                const elementModuleFactory = yield this.getElementModuleFactory(elementModule);
                const elementModuleRef = elementModuleFactory.create(this.injector);
                const elementComponentTypes = elementModuleRef._bootstrapComponents;
                if (!elementComponentTypes.length)
                    throw new Error(Errors.BootstrapElement);
                else
                    for (const elementComponentType of elementComponentTypes)
                        yield this.createElement(elementRoute.tagName, elementComponentType);
                // Remove compiling class
                customElement.classList.remove("compiling");
            }
            catch (error) {
                // Add error class
                customElement.classList.remove("compiling");
                customElement.classList.add("failed");
                if (!this.environment.livemode)
                    customElement.innerHTML = `<code><strong>Vattenfall Element</strong><br><br>${error}</code>`;
            }
        });
    }
    /**
     * Loads element module from `ElementRoute` and throw an error if unsuccessful
     * @param elementRoute The `ElementRoute` object
     */
    getElementModule(elementRoute) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (elementRoute.loadChildren)
                    return yield elementRoute.loadChildren();
            }
            catch (error) {
                throw new Error(`${Errors.LoadModule} Failed with exception: ${error.message || error}`);
            }
            if (!elementRoute.elementModule)
                throw new Error(Errors.NoModule);
            return elementRoute.elementModule;
        });
    }
    /**
     * Compiles element module from `ElementRoute` and throw an error if unsuccessful
     * @param elementRoute The `ElementRoute` object
     */
    getElementModuleFactory(elementModule) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (elementModule instanceof NgModuleFactory)
                    return elementModule;
                else {
                    return yield this.compiler.compileModuleAsync(elementModule);
                }
            }
            catch (error) {
                throw new Error(`${Errors.CompileModule} Failed with exception: ${error.message || error}`);
            }
        });
    }
}
ElementsRootModule.ɵmod = ɵɵdefineNgModule({ type: ElementsRootModule });
ElementsRootModule.ɵinj = ɵɵdefineInjector({ factory: function ElementsRootModule_Factory(t) { return new (t || ElementsRootModule)(ɵɵinject(Injector), ɵɵinject(Compiler), ɵɵinject(ComponentFactoryResolver), ɵɵinject(ELEMENT_ROUTES, 8), ɵɵinject(ENVIRONMENT, 8)); } });
/*@__PURE__*/ (function () { ɵsetClassMetadata(ElementsRootModule, [{
        type: NgModule
    }], function () { return [{ type: Injector }, { type: Compiler }, { type: ComponentFactoryResolver }, { type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [ELEMENT_ROUTES]
            }] }, { type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [ENVIRONMENT]
            }] }]; }, null); })();

// A dummy custom element to be used as the root element and component host view
class RootElement extends HTMLElement {
    constructor() {
        super();
    }
}
/**
 * A DOM based implementation of the `TestComponentRenderer` for custom elements.
 *
 * This class is used in place of the `DOMTestComponentRenderer` which creates a div element with a unique id in the document body,
 * and then creates a component with that div element as its application host view. This will not work with custom elements,
 * because the custom element is defined by its tag name.
 *
 * By overriding the `DOMTestComponentRenderer`, this class makes sure that the root element is a custom element and that the test component
 * is created inside that element. This allows for both testing the component as a `ComponentFixture` and still use `slot` elements
 * and other web component utilities.
 */
class DOMTestElementRenderer extends TestComponentRenderer {
    constructor(document, tagName) {
        super();
        this.document = document;
        this.tagName = tagName;
    }
    // This method will be invoked by the `TestBed` `createComponent` method before the component is created,
    // and the `ComponentFixture` is returned
    insertRootElement(rootElementId) {
        if (!this.tagName)
            throw new Error(`[ Vattenfall Elements ] – No tag name provided. Could not insert custom root element.`);
        if (!customElements.get(this.tagName)) {
            // Define the `RootElement` as a custom element in the browser custom elements registry
            customElements.define(this.tagName, RootElement);
        }
        const rootElement = this.document.createElement(this.tagName);
        rootElement.id = rootElementId;
        this.document.body.appendChild(rootElement);
    }
}
DOMTestElementRenderer.ɵfac = function DOMTestElementRenderer_Factory(t) { return new (t || DOMTestElementRenderer)(ɵɵinject(DOCUMENT), ɵɵinject("tagName")); };
DOMTestElementRenderer.ɵprov = ɵɵdefineInjectable({ token: DOMTestElementRenderer, factory: DOMTestElementRenderer.ɵfac });
/*@__PURE__*/ (function () { ɵsetClassMetadata(DOMTestElementRenderer, [{
        type: Injectable
    }], function () { return [{ type: Document, decorators: [{
                type: Inject,
                args: [DOCUMENT]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: ["tagName"]
            }] }]; }, null); })();
let elementsTestBed;
function getElementsTestBed() {
    return (elementsTestBed = elementsTestBed || new ElementsTestBed());
}
/**
 * The `ElementsTestBed` is the primary API for writing unit tests for elements, components and services in the Vattenfall Elements framework.
 *
 * I inherits the same functionality as the standard Angular TestBed, but extends it with methods for testing custom elements and other related web component technologies.
 */
class ElementsTestBed {
    /**
     * Creates a component as a child of a custom element and return that component as a `ComponentFixture`.
     *
     * Please note that similarly to the `createComponent` method, the first time this method is invoked the environment for unit testing stays fixed to that specific configuration,
     * and that this method returns a Promise since the custom elements definition is an async process.
     *
     * @param component The component to create
     */
    static createElement(component) {
        return __awaiter(this, void 0, void 0, function* () {
            const componentSelector = ElementsTestBed.componentSelector(component);
            // This override the `TestComponentRenderer` which is responsible for creating
            // a root element which the `createComponent` method will use to attach the component into,
            // with a custom built renderer for custom elements
            TestBed.overrideProvider(TestComponentRenderer, {
                useFactory: (document) => new DOMTestElementRenderer(document, componentSelector),
                deps: [DOCUMENT],
            });
            // This sets the inital state for the element
            if (getElementsTestBed().state)
                ElementsTestBed.configureState(getElementsTestBed().state);
            // Create a component fixture with the `DOMTestElementRenderer` now intercepting root element creating step
            const fixture = TestBed.createComponent(component);
            // Append the slotable elements as children to the custom element
            if (getElementsTestBed().slotableElements)
                if (typeof getElementsTestBed().slotableElements === "string") {
                    fixture.nativeElement.innerHTML = getElementsTestBed().slotableElements;
                }
                else
                    Object.values(getElementsTestBed().slotableElements).forEach((slotableElement) => fixture.nativeElement.appendChild(slotableElement));
            // Await the custom element to be defined in the browser custom elements registry
            // and then return its component fixture
            if (componentSelector)
                yield customElements.whenDefined(componentSelector);
            return fixture;
        });
    }
    /**
     * Returns a component's selector from its class annotation
     * @param component The component to get the selector from
     */
    static componentSelector(component) {
        const annotations = Object.getOwnPropertyDescriptor(component, "__annotations__");
        if (annotations)
            return annotations.value[0].selector;
        else
            return undefined;
    }
    /**
     * Takes a list of slotable element objects and generates a list of real HTML elements.
     *
     * **Usage**
     *
     * ```html
     * <!--
     *  Default: "someSlotableElementRef": "Some slotted value"
     * -->
     * <span slot="someSlotableElementRef">Some slotted value</span>
     * <!--
     *  Custom tag: "someSlotableElementRef": { tagName: "header", value: "Some slotted value" }
     * -->
     * <header slot="someSlotableElementRef">Some slotted value</header>
     * <!--
     *  Custom attribute: "someSlotableElementRef": { tagName: "img", attr: { src: "/path/to/image.png" } }
     * -->
     * <img slot="someSlotableElementRef" srf="/path/to/image.png"/>
     * ```
     *
     * @param slotableElements The slotable objects to generate HTML elements from
     */
    static generateSlotableElements(slotableElementProps) {
        const slotableElements = {};
        for (const slotRef in slotableElementProps) {
            const slotValueOrObject = slotableElementProps[slotRef];
            // If the value is a string then use <span>
            const tagName = typeof slotValueOrObject === "string" || !slotValueOrObject.tagName ? "span" : slotValueOrObject.tagName;
            const attributes = typeof slotValueOrObject !== "string" && slotValueOrObject.attr ? Object.entries(slotValueOrObject.attr) : [];
            const value = typeof slotValueOrObject === "string" ? slotValueOrObject : slotValueOrObject.value || "";
            const slotableElement = document.createElement(tagName);
            attributes.forEach(([attributeName, attributeValue]) => slotableElement.setAttribute(attributeName, attributeValue));
            slotableElement.setAttribute("slot", slotRef);
            slotableElement.textContent = value;
            slotableElements[slotRef] = slotableElement;
        }
        return slotableElements;
    }
    /**
     * Extracts slot elements from a HTML string or HTMLElement
     * @param htmlStringOrElement The HTML string or HTMLElement to extract the slots elements from
     */
    static extractSlotElements(htmlStringOrElement) {
        const slotElements = {};
        const element = document.createElement("template");
        if (typeof htmlStringOrElement === "string")
            element.innerHTML = htmlStringOrElement;
        else
            element.appendChild(htmlStringOrElement);
        element.querySelectorAll("slot").forEach((slotElement) => {
            const slotRef = slotElement.getAttribute("name") || "__empty__";
            slotElements[slotRef] = slotElement;
        });
        return slotElements;
    }
    /**
     * Sets the test modules boostrap configuration options.
     *
     * **Environment**
     *
     * This would be useful when testing an element's behaviour in for example different locales.
     *
     * The config argument reflects the same as when the application config used when bootstraping the application i.e. `new Elements({ ... })`; however,
     * by providing the environment object you may overwrite any of these arguments if needed, for example when testing that a service throws an error
     * if an incorrect URL path has been provided.
     *
     * **State**
     *
     * This is useful when setting an initial state of the application before components are created and loaded in view.
     * Please note that you need to provide the registered state name as key, as this method will set the entire global state.
     *
     * **Slots**
     *
     * This allows you to pass slotable elements to the element.
     *
     * @param elementDef The element's bootstraping configurations
     */
    static configureElements(elementDef) {
        // Set the environment configs
        {
            getElementsTestBed().environment = {
                livemode: false,
                locale: elementDef.environment.locale,
                language: elementDef.environment.language
                    ? elementDef.environment.language
                    : elementDef.environment.locale.split("-")[0].toLowerCase(),
                region: elementDef.environment.region
                    ? elementDef.environment.region
                    : elementDef.environment.locale.split("-")[1].toLowerCase(),
                endpoints: elementDef.environment.endpoints
                    ? elementDef.environment.endpoints
                    : {
                        selfServiceApi: "/elements",
                        acquisitionApi: "/api/acquisition",
                    },
                trackingId: elementDef.environment.trackingId,
                viewId: elementDef.environment.viewId,
            };
        }
        // Set slot elements
        if (elementDef.slotableElements) {
            if (typeof elementDef.slotableElements !== "string")
                getElementsTestBed().slotableElements = ElementsTestBed.generateSlotableElements(elementDef.slotableElements);
            else
                getElementsTestBed().slotableElements = elementDef.slotableElements;
        }
        // Sets the inital state
        if (elementDef.state) {
            getElementsTestBed().state = elementDef.state;
        }
        return ElementsTestBed;
    }
    /**
     * Sets or resets the state of the application.
     *
     * This is useful when preparing the state for the next operation.
     * Please note that you need to provide the registered state name as key, as this method will set the entire global state.
     *
     * The state will not be patched, but reset everytime this method is called.
     *
     * @param state The global state to set
     */
    static configureState(state) {
        // Should save the state and take from that when the app is initialized
        getElementsTestBed().store = getElementsTestBed().store || TestBed.inject(Store);
        getElementsTestBed().store.reset(state);
    }
    /**
     * Dispatches a state action and returns a promise which resolves once the dispatch has completed.
     * @param action The action to dispatch
     */
    static dispatchAction(action) {
        return __awaiter(this, void 0, void 0, function* () {
            yield getElementsTestBed().store.dispatch(action).toPromise();
        });
    }
    /**
     * Allows overriding default providers, directives, pipes, modules of the test injector, which are defined in test_injector.js
     */
    static configureTestingModule(moduleDef) {
        return TestBed.configureTestingModule(Object.assign({
            // TODO (stefan): Add LOCALE provider as well in order to test elements using different locale settings
            providers: [
                {
                    provide: ENVIRONMENT,
                    useValue: getElementsTestBed().environment,
                },
            ].concat(moduleDef.providers || []),
        }, moduleDef));
    }
}
/**
 * For checking whether an HTMLSlotElement has any nodes assigned to it
 */
if ("expect" in window)
    expect.extend({
        toBeAssigned(received) {
            if (received.assignedNodes().length) {
                return {
                    message: () => `expected HTMLSlotElement '${received.name}' not to have assigned nodes`,
                    pass: true,
                };
            }
            else {
                return {
                    message: () => `expected HTMLSlotElement '${received.name}' to have assigned nodes`,
                    pass: false,
                };
            }
        },
    });
/**
 * For checking whether an HTMLSlotElement has any nodes assigned to it or if the slot element has been duplicated
 */
if ("expect" in window)
    expect.extend({
        toBeAssignedOrDuplicated(received) {
            let duplicatedSlot;
            if (!received.assignedNodes().length) {
                const slottableElements = Array.from(received.getRootNode().host.children);
                duplicatedSlot = Array.from(received.children).some((childElement) => slottableElements.some((slottableElement) => childElement.innerHTML === slottableElement.innerHTML));
            }
            if (received.assignedNodes().length || duplicatedSlot) {
                return {
                    message: () => `expected HTMLSlotElement '${received.name}' not to have assigned nodes`,
                    pass: true,
                };
            }
            else {
                return {
                    message: () => `expected HTMLSlotElement '${received.name}' to have assigned nodes`,
                    pass: false,
                };
            }
        },
    });

class CookieInterceptor {
    intercept(request, next) {
        const newRequest = request.clone({
            withCredentials: true,
            /**
             * We need to clear the cache, and set expires to a past date because of a bug in IE11.
             * IE11 caches the last call to the Self Service API and returns the same results as the previous login
             * So in case you login with two different customers, the second login will show the same contract as the first login.
             * By setting no-cache, we make lazy IE11 call every time and do its job.
             */
            headers: request.headers
                .set("Cache-Control", "no-cache")
                .set("Pragma", "no-cache")
                .set("Expires", "Thu, 01 Jan 1970 00:00:00 GMT")
                .set("If-Modified-Since", "0"),
        });
        return next.handle(newRequest);
    }
}
CookieInterceptor.ɵfac = function CookieInterceptor_Factory(t) { return new (t || CookieInterceptor)(); };
CookieInterceptor.ɵprov = ɵɵdefineInjectable({ token: CookieInterceptor, factory: CookieInterceptor.ɵfac });
/*@__PURE__*/ (function () { ɵsetClassMetadata(CookieInterceptor, [{
        type: Injectable
    }], null, null); })();
const cookieInterceptorProvider = {
    provide: HTTP_INTERCEPTORS,
    useClass: CookieInterceptor,
    multi: true,
};

/**
 * The default service response delay time expressed in milliseconds
 */
const SERVICE_DELAY_TIME = 500;
/**
 * TODO (stefan): Check if and where this is referenced, if not then it could be removed.
 * Simulate HTTP response
 * @param status HTTP response status code
 * @param body HTTP response body
 * @return HTTP response
 */
const httpResponseSimulation = (status, body) => {
    if (status >= 200 && status <= 300)
        return of(new HttpResponse({ status, body })).pipe(delay(SERVICE_DELAY_TIME));
    else
        return throwError(new HttpErrorResponse({ status })).pipe(delay(SERVICE_DELAY_TIME));
};
/**
 * HTTP status code
 */
var HttpStatusCode;
(function (HttpStatusCode) {
    HttpStatusCode[HttpStatusCode["Success"] = 200] = "Success";
    HttpStatusCode[HttpStatusCode["BadRequest"] = 400] = "BadRequest";
    HttpStatusCode[HttpStatusCode["NotFound"] = 404] = "NotFound";
    HttpStatusCode[HttpStatusCode["MethodNotAllowed"] = 422] = "MethodNotAllowed";
    HttpStatusCode[HttpStatusCode["ServerError"] = 500] = "ServerError";
})(HttpStatusCode || (HttpStatusCode = {}));
/**
 * Intercepts requests and returns mockup data as response.
 *
 * You may override the `onGet`, `onGetAll`, `onPost`, `onPut` and `onDelete` request handlers in your child class to better match the actual endpoint behaviour.
 *
 * This will override the `onDelete` method and return `422 Method Not Allowed` on all requests made to this endpoint with the method `DELETE`:
 *
 * ```typescript
 * onDelete() {
 *   return super.httpResponse(HttpStatusCode.MethodNotAllowed)
 * }
 * ```
 */
class MockupHttpInterceptor {
    constructor(mockup) {
        this.mockup = mockup;
        if (!this.mockup.pathMatcher)
            throw new Error(`[ Vattenfall Elements ] Could not initialize 'MockupHttpInterceptor'. 'pathMatcher' property is undefined.`);
        if (!this.mockup.pathMatcher.startsWith("/"))
            throw new Error(`[ Vattenfall Elements ] Could not initialize 'MockupHttpInterceptor'. 'pathMatcher' property needs to start with '/'.`);
        if (!this.mockup.data.length)
            throw new Error(`[ Vattenfall Elements ] Could not initialize 'MockupHttpInterceptor'. 'data' array is empty.`);
    }
    /**
     * Intercept HTTP request and return appropriate HTTP response
     * @param request HTTP request object
     */
    intercept(request, next) {
        const { method, url, body } = request;
        if (!this.matchUrlPath(this.mockup.pathMatcher, url))
            return next.handle(request);
        const params = this.parseParams(this.mockup.pathMatcher, url);
        switch (method) {
            case "GET": {
                return this.onGet(params);
            }
            case "POST": {
                return this.onPost(body);
            }
            case "PUT": {
                return this.onPut(body, params);
            }
            case "DELETE": {
                return this.onDelete(params);
            }
            // Method Not Allowed
            default:
                return this.httpResponse(HttpStatusCode.MethodNotAllowed);
        }
    }
    /**
     * Simulate HTTP response
     * @param status HTTP response status code
     * @param body HTTP response body
     */
    httpResponse(status, body) {
        if (status >= 200 && status <= 300)
            return of(new HttpResponse({ status, body })).pipe(delay(this.mockup.delay));
        else {
            return throwError(new HttpErrorResponse({
                error: `Error ${status}`,
                status,
            })).pipe(delay(this.mockup.delay));
        }
    }
    /**
     * Get request handler
     * @param param Parameter hodling the key and value to query the data.
     */
    onGet(params) {
        const payload = this.mockup.data.filter(this.matchIdentifier(params));
        if (payload.length === 1 || this.mockup.limit === 1)
            return this.httpResponse(HttpStatusCode.Success, payload[0]);
        else if (payload.length > 1)
            return this.httpResponse(HttpStatusCode.Success, payload.slice(0, this.mockup.limit || payload.length));
        else
            return this.httpResponse(HttpStatusCode.NotFound, payload);
    }
    /**
     * Post request handler
     * @param payload the payload to update the item.
     */
    onPost(payload) {
        if (!payload)
            return this.httpResponse(HttpStatusCode.BadRequest, payload);
        else {
            this.mockup.data = [...this.mockup.data, payload];
            return this.httpResponse(HttpStatusCode.Success, payload);
        }
    }
    /**
     * Put request handler
     * @param param key, value parameter to query the data
     * @param payload the payload to update the item.
     */
    onPut(payload, params) {
        if (!payload || !params)
            return this.httpResponse(HttpStatusCode.BadRequest, payload);
        else if (this.mockup.data.some(this.matchIdentifier(params))) {
            this.mockup.data = this.mockup.data.map((item) => this.matchIdentifier(params)(item) ? Object.assign(item, payload) : item);
            return this.httpResponse(HttpStatusCode.Success, payload);
        }
        else
            return this.httpResponse(HttpStatusCode.NotFound, payload);
    }
    /**
     * Delete request handler
     * @param param parameter with key, value to query and delete the item in the data.
     */
    onDelete(params) {
        if (this.mockup.data.some(this.matchIdentifier(params))) {
            this.mockup.data = this.mockup.data.filter(this.matchIdentifier(params));
            return this.httpResponse(HttpStatusCode.Success);
        }
        else
            return this.httpResponse(HttpStatusCode.NotFound);
    }
    /**
     * Match provided resource identifiers or query parameters against data items in `mockup` array. Will match data items which does not contain properties that might be specified in `params`.
     * @param parameters
     */
    matchIdentifier(params = {}) {
        return (item = {}) => Object.entries(params)
            // Testing for loose equality to match numeric values cast as strings, i.e. "123"
            .reduce((match, [key, value]) => (!item[key] || item[key] == value ? match && true : false), true);
    }
    /**
     * Compares request URL with specified path matcher string
     * @param pathMatcher path matcher pattern to compare to the url
     * @param url url for the current request.
     */
    matchUrlPath(pathMatcher, url) {
        const matcherSegments = pathMatcher.split("/");
        const urlSegments = new URL(url, "https://localhost:4200").pathname.split("/");
        // Do not match URL's that have more segments than declared in pathMatcher
        if (matcherSegments.length < urlSegments.length)
            return false;
        // Do not match if any of the URL segments does not match the segments in pathMatcher
        for (let i = 0; i < matcherSegments.length; i++) {
            if (!matcherSegments[i].startsWith(":"))
                if (matcherSegments[i] !== urlSegments[i])
                    return false;
        }
        return true;
    }
    /**
     * Takes an URL and parse it against a matching string. Returns an object with the resource name or query parameter name as key and resource identifier query parameter value as value.
     * @param pathMatcher path matcher pattern to compare to the url
     * @param url url for the request
     *
     * **Example**
     *
     * This input:
     *
     * * pathMatcher: `component/:componentId`
     * * url: `component/123&queryParam=abc`
     *
     * Will give this output:
     *
     * ```
     * { componentId: "123", queryParam: "abc" }
     * ```
     */
    parseParams(pathMatcher, url) {
        const urlSegments = new URL(url, "https://localhost:4200").pathname
            .split("/")
            .map((path, index) => [
            new URL(pathMatcher, "https://localhost:4200").pathname.split("/")[index].replace(":", ""),
            path,
        ])
            .filter(([key, value]) => key !== value);
        const urlSearchParams = Array.from(new URLSearchParams(new URL(url, "https://localhost:4200").searchParams).entries());
        if (urlSegments.length || urlSearchParams.length)
            return Object.fromEntries([...urlSegments, ...urlSearchParams]);
    }
}

/**
 * Takes either a date object or an integer value indicating a specific month of the year and return the name of that month. The unit pipe will produce different outputs depending on the locale of current runtime environment.
 *
 * ### Usage
 *
 * **With a integer value**
 *
 * ```html
 * input: 3 | month
 * output: Mars
 * ```
 *
 * **With a date object**
 *
 * ```html
 * input: date | month
 * output: Mars
 * ```
 */
class MonthPipe {
    constructor(datePipe) {
        this.datePipe = datePipe;
    }
    /**
     * @param month The month represented as a integer value or date object to return.
     */
    transform(month) {
        if (month == null || month === "")
            return null;
        if (month instanceof Date)
            return this.datePipe.transform(month, "LLLL");
        if (![1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].includes(coerceNumberProperty(month)))
            throw new Error(`[ Vattenfall Elements ] Invalid pipe argument for pipe 'month'. The provided month '${month}' is invalid, must be an integer in the range of 1 to 12.`);
        const date = new Date(0);
        date.setMonth(coerceNumberProperty(month), 0);
        return this.datePipe.transform(date, "LLLL");
    }
}
MonthPipe.ɵfac = function MonthPipe_Factory(t) { return new (t || MonthPipe)(ɵɵdirectiveInject(DatePipe)); };
MonthPipe.ɵpipe = ɵɵdefinePipe({ name: "month", type: MonthPipe, pure: true });
/*@__PURE__*/ (function () { ɵsetClassMetadata(MonthPipe, [{
        type: Pipe,
        args: [{
                name: "month",
            }]
    }], function () { return [{ type: DatePipe }]; }, null); })();

/**
 * 'pickAll' Returns a partial copy of an object containing only the keys specified and key: undefined pair for properties that don't exist.
 * Usage: contract | pickAll:['contractStartDate', 'contractEndDate']
 * Returns: an object with properties {contractStartDate, contractEndDate } of contract object
 * Example:
 * <p> {{ contract | pickAll:['contractStartDate','contractEndDate'] }}</p>
 * will create an object with 'contractStartDate' and 'contractEndDate' properties,
 * which then can be used as input into another pipe.
 */
class PickAllPipe {
    transform(obj, props) {
        /**
         * Ramda: `pickAll` doc: https://ramdajs.com/docs/#pickAll
         * Returns a partial copy of an object containing only the keys specified.
         * If the key does not exist, the property is ignored.
         */
        return pickAll(props, obj);
    }
}
PickAllPipe.ɵfac = function PickAllPipe_Factory(t) { return new (t || PickAllPipe)(); };
PickAllPipe.ɵpipe = ɵɵdefinePipe({ name: "pickAll", type: PickAllPipe, pure: true });
/*@__PURE__*/ (function () { ɵsetClassMetadata(PickAllPipe, [{
        type: Pipe,
        args: [{
                name: "pickAll",
            }]
    }], null, null); })();

/**
 * timeRange pipe is used to print out a time range given 2 sets of dates
 * Input: object with unix dates as properties.
 * Outputs: string -> dd/mm/YYYY - dd/mm/YYYY or Error.
 * Usage: {startdate, enddate} | timeRange
 */
class TimeRangePipe {
    transform(dates) {
        let rangeText = "";
        if (Object.keys(dates).length < 2) {
            return new Error("To create a time range, at least two dates must be passed.");
        }
        Object.keys(dates).forEach((key) => {
            const currentDate = dates[key];
            // Add ' - ' after the first date.
            if (rangeText !== "")
                rangeText += " — ";
            // TODO: Improve this pipe with support for both Finnish and Swedish locales
            rangeText += currentDate ? new Date(currentDate * 1000).toLocaleDateString("sv-SE") : "Tillsvidare";
        });
        return rangeText;
    }
}
TimeRangePipe.ɵfac = function TimeRangePipe_Factory(t) { return new (t || TimeRangePipe)(); };
TimeRangePipe.ɵpipe = ɵɵdefinePipe({ name: "timeRange", type: TimeRangePipe, pure: true });
/*@__PURE__*/ (function () { ɵsetClassMetadata(TimeRangePipe, [{
        type: Pipe,
        args: [{
                name: "timeRange",
            }]
    }], null, null); })();

/**
 * List of units supported by this application
 */
const UNITS = [
    "KWH",
    "MWH",
    "GWH",
    "SWEDISH_KRONA",
    "SWEDISH_ORE/KWH",
    "SWEDISH_KRONA/YEAR",
    "SWEDISH_KRONA/MONTH",
    "INVOICES_PER_YEAR",
    "KWH_PER_YEAR",
    "MWH_PER_YEAR",
    "SWEDISH_KRONA/MWH",
    "SWEDISH_KRONA/CUBIC_METER",
    "UNIT_UNKNOWN",
    "EURO/MONTH",
    "EURO_CENT/KWH",
    "MONTH",
    "MONTHS",
    "KG/MONTH",
    "DAYS_REMAINING",
];
/**
 * Resolves rest parameters into key-value tuple
 * @param decimalPipe Decimal pipe transform function to format value with
 * @param parameters Parameters as passed into the pipe
 */
function resolveParameters(decimalPipe, parameters) {
    var _a, _b;
    // Returns true if value is a unit-value object
    const isUnitValue = (value) => (value === null || value === void 0 ? void 0 : value.value) != null && isUnitValueKey(value === null || value === void 0 ? void 0 : value.unit);
    // Returns true if value is a unit-value key
    const isUnitValueKey = (value) => UNITS.includes(value);
    if (parameters.length) {
        if (isUnitValue(parameters[0])) {
            return [
                decimalPipe.transform(coerceNumberProperty(parameters[0].value), (_a = parameters[1]) !== null && _a !== void 0 ? _a : null),
                parameters[0].unit,
            ];
        }
        else if (isUnitValueKey(parameters[1])) {
            return [decimalPipe.transform(coerceNumberProperty(parameters[0]), (_b = parameters[2]) !== null && _b !== void 0 ? _b : null), parameters[1]];
        }
    }
    throw new Error(`[ Vattenfall Elements ] Invalid pipe argument for pipe 'unit'. Expect a 'UnitValue' object or a number followed by a unit parameter. Got ${JSON.stringify(parameters)}`);
}
/**
 * The unit pipe will take unit-value and parse it into a string that is meaningful for the end user.
 *
 * It takes either a `UnitValue` object or a numeric value as input, if the later then a `key` parameter
 * must be provided. Optionally, a digits parameter may be provided which parse the numeric value
 * into the specified format.
 *
 * The unit pipe will produce different outputs depending on the locale of current runtime environment.
 *
 * ### Usage
 *
 * **With a unit-value object as input**
 *
 * ```html
 * input: { key: "KWH_PER_YEAR", value: 12532 } | unit
 * output: 12 523 kWh/år
 * ```
 *
 * **With a numeric value as input**
 *
 * ```html
 * input: 13.43 | unit : 'EURO/MONTH' }}
 * output: 13.43 €/kk
 * ```
 *
 * **With a digits parameter**
 *
 * ```html
 * input: 13.43 | unit : '1.1-1'
 * output: 13.4 €/kk
 * ```
 */
class UnitPipe {
    constructor(environment, decimalPipe) {
        this.environment = environment;
        this.decimalPipe = decimalPipe;
    }
    /**
     * @param input The unit-value object or number to be formatted.
     * @param unit A unit key used for parsing the value. Required if the provided input is a number.
     * @param digitsInfo Decimal representation options, specified by a string
     * in the following format:<br>
     * <code>{minIntegerDigits}.{minFractionDigits}-{maxFractionDigits}</code>.
     *   - `minIntegerDigits`: The minimum number of integer digits before the decimal point.
     * Default is `1`.
     *   - `minFractionDigits`: The minimum number of digits after the decimal point.
     * Default is `0`.
     *   - `maxFractionDigits`: The maximum number of digits after the decimal point.
     * Default is `3`.
     */
    transform(...parameters) {
        if (!parameters.length || parameters[0] == null)
            return null;
        const [value, unit] = resolveParameters(this.decimalPipe, parameters);
        switch (this.environment.language) {
            case "sv": {
                switch (unit) {
                    case "KWH":
                        return `${value} kWh`;
                    case "MWH":
                        return `${value} MWh`;
                    case "GWH":
                        return `${value} GWh`;
                    case "SWEDISH_KRONA":
                        return `${value} kr`;
                    case "SWEDISH_ORE/KWH":
                        return `${value} öre/kWh`;
                    case "SWEDISH_KRONA/YEAR":
                        return `${value} kr/år`;
                    case "SWEDISH_KRONA/MONTH":
                        return `${value} kr/mån`;
                    case "INVOICES_PER_YEAR":
                        return Number(value) > 1 ? `${value} fakturor per år` : `${value} faktura per år`;
                    case "KWH_PER_YEAR":
                        return `${value} kWh/år`;
                    case "MWH_PER_YEAR":
                        return `${value} MWh/år`;
                    case "SWEDISH_KRONA/MWH":
                        return `${value} kr/MWh`;
                    case "SWEDISH_KRONA/CUBIC_METER":
                        return `${value} kr/m<sup>3</sup>`;
                    case "UNIT_UNKNOWN":
                        return `${value}`;
                    case "EURO/MONTH":
                        return `${value} €/mån`;
                    case "EURO_CENT/KWH":
                        return `${value} c/kWh`;
                    case "MONTH":
                        return `${value} mån`;
                    case "MONTHS":
                        return `${value} mån`;
                    case "KG/MONTH":
                        return `${value} kg/mån`;
                    case "DAYS_REMAINING":
                        return Number(value) === 0 ? `idag` : Number(value) === 1 ? `${value} dag kvar` : `${value} dagar kvar`;
                    default:
                        throw new Error(`[ Vattenfall Elements ] Invalid pipe argument for pipe 'unit'. The unit '${unit}' is not supported for this locale ${this.environment.locale}.`);
                }
            }
            case "fi": {
                switch (unit) {
                    case "KWH":
                        return `${value} kWh`;
                    case "MWH":
                        return `${value} MWh`;
                    case "GWH":
                        return `${value} GWh`;
                    case "UNIT_UNKNOWN":
                        return `${value}`;
                    case "EURO/MONTH":
                        return `${value} €/kk`;
                    case "EURO_CENT/KWH":
                        return `${value} snt/kWh`;
                    case "MONTH":
                        return `${value} kk`;
                    case "MONTHS":
                        return `${value} kk`;
                    case "KG/MONTH":
                        return `${value} kg/kk`;
                    default:
                        throw new Error(`[ Vattenfall Elements ] Invalid pipe argument for pipe 'unit'. The unit '${unit}' is not supported for this locale ${this.environment.locale}.`);
                }
            }
            default:
                throw new Error(`[ Vattenfall Elements ] Invalid pipe argument for pipe 'unit'. The locale '${this.environment.locale}' is not supported by this pipe.`);
        }
    }
}
UnitPipe.ɵfac = function UnitPipe_Factory(t) { return new (t || UnitPipe)(ɵɵdirectiveInject(ENVIRONMENT, 8), ɵɵdirectiveInject(DecimalPipe)); };
UnitPipe.ɵpipe = ɵɵdefinePipe({ name: "unit", type: UnitPipe, pure: true });
/*@__PURE__*/ (function () { ɵsetClassMetadata(UnitPipe, [{
        type: Pipe,
        args: [{
                name: "unit",
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [ENVIRONMENT]
            }] }, { type: DecimalPipe }]; }, null); })();

class PipesModule {
}
PipesModule.ɵmod = ɵɵdefineNgModule({ type: PipesModule });
PipesModule.ɵinj = ɵɵdefineInjector({ factory: function PipesModule_Factory(t) { return new (t || PipesModule)(); }, providers: [DecimalPipe, DatePipe], imports: [[CommonModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵɵsetNgModuleScope(PipesModule, { declarations: [UnitPipe, TimeRangePipe, PickAllPipe, MonthPipe], imports: [CommonModule], exports: [UnitPipe, TimeRangePipe, PickAllPipe, DatePipe, MonthPipe] }); })();
/*@__PURE__*/ (function () { ɵsetClassMetadata(PipesModule, [{
        type: NgModule,
        args: [{
                declarations: [UnitPipe, TimeRangePipe, PickAllPipe, MonthPipe],
                imports: [CommonModule],
                exports: [UnitPipe, TimeRangePipe, PickAllPipe, DatePipe, MonthPipe],
                providers: [DecimalPipe, DatePipe],
            }]
    }], null, null); })();

/**
 * Dispatches events through the data layer to a Google Tag Manager container.
 *
 * The data layer is an array of objects used to pass information from a website to external services.
 *
 * It is essentially a message bus for web site data such as product data, user interaction data, marketing campaign information, transaction data, etc. and the web site external data processing services.
 */
class DataLayerService {
    constructor(environment, actions) {
        this.environment = environment;
        this.actions = actions;
        /**
         * Data model for dispatching enhanced ecommerce field objects
         */
        this.ecommerce = new Ecommerce();
        // Sets a data layer property on the window object
        window.dataLayer = window.dataLayer || [];
        // Dispatch an elements initialized event
        this.dispatch(Object.assign({ event: "Elements@Initialized" }, environment));
        // If actions has been provided then tap into the actions stream
        if (this.actions)
            // Dispatch every time an action has been dispatched
            this.actions
                .pipe(
            // Ignore failed or canceled actions
            filter(({ status }) => status === "SUCCESSFUL"), 
            // Ignore system actions
            filter(({ action }) => !action.constructor.type.startsWith("@@")), 
            // Map to a standard event object
            map(({ action }) => {
                // Turns an action type string and turns it into an event string: [ TheElementClassName ] NameOfAction > TheElementClassName.NameOfAction
                const eventName = action.constructor.type.replace(/ /g, "").replace("[", "").replace("]", ".");
                return Object.assign({ event: `Elements@${eventName}` }, action);
            }), 
            // Ignore empty events
            filter((event) => event != null)
            // Dispatch action as event
            )
                .subscribe(this.dispatch);
    }
    /**
     * Dispatches an event to the data layer
     * @param event An event object (should typically have an `event` property but it is not required)
     */
    dispatch(event) {
        // Prefix the event name with Elements namespace
        if (event.event && !event.event.startsWith("Elements@"))
            event.event = `Elements@${event.event}`;
        // Dispatch the event to the data layer
        window.dataLayer.push(event);
        // Return the service to enable method chaining
        return this;
    }
}
DataLayerService.ɵfac = function DataLayerService_Factory(t) { return new (t || DataLayerService)(ɵɵinject(ENVIRONMENT, 8), ɵɵinject(Actions, 8)); };
DataLayerService.ɵprov = ɵɵdefineInjectable({ token: DataLayerService, factory: DataLayerService.ɵfac, providedIn: "root" });
/*@__PURE__*/ (function () { ɵsetClassMetadata(DataLayerService, [{
        type: Injectable,
        args: [{
                providedIn: "root",
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [ENVIRONMENT]
            }] }, { type: Actions, decorators: [{
                type: Optional
            }] }]; }, null); })();
/**
 * `Ecommerce` specify how to interpret product and promotion data that you send to Google Analytics.
 */
class Ecommerce {
    /**
     * An impression of a for one or more products.
     * @param impression Product impression field object
     */
    impression(impression) {
        window.dataLayer.push({
            event: "Elements@Ecommerce.Impression",
            ecommerce: {
                impressions: [impression],
            },
        });
        this.impressionFieldObject = impression;
        return Ecommerce;
    }
    /**
     * A click on a product or product link for one or more products.
     * @param product Product field object
     * @param action Action field object
     */
    click(product, action) {
        window.dataLayer.push({
            event: "Elements@Ecommerce.Click",
            ecommerce: {
                click: {
                    actionField: action,
                    products: [product],
                },
            },
        });
        this.productFieldObject = product;
        return Ecommerce;
    }
    /**
     * A view of product details.
     * @param product Product field object
     * @param action Action field object
     */
    detail(product, action) {
        window.dataLayer.push({
            event: "Elements@Ecommerce.Detail",
            ecommerce: {
                detail: {
                    actionField: action,
                    products: [product],
                },
            },
        });
        this.productFieldObject = product;
        return Ecommerce;
    }
    /**
     * Adding one or more products to a shopping cart.
     * @param product Product field object
     * @param action Action field object
     */
    add(product, action) {
        window.dataLayer.push({
            event: "Elements@Ecommerce.Add",
            ecommerce: {
                add: {
                    actionField: action,
                    products: [product],
                },
            },
        });
        this.productFieldObject = product;
        return Ecommerce;
    }
    /**
     * Remove one or more products from a shopping cart.
     * @param product Product field object
     * @param action Action field object
     */
    remove(product, action) {
        window.dataLayer.push({
            event: "Elements@Ecommerce.Remove",
            ecommerce: {
                remove: {
                    actionField: action,
                    products: [product],
                },
            },
        });
        this.productFieldObject = product;
        return Ecommerce;
    }
    /**
     * Initiating the checkout process for one or more products.
     * @param product Product field object
     * @param action Action field object
     */
    checkout(product, action) {
        window.dataLayer.push({
            event: "Elements@Ecommerce.Checkout",
            ecommerce: {
                checkout: {
                    actionField: action,
                    products: [product],
                },
            },
        });
        this.productFieldObject = product;
        return Ecommerce;
    }
    /**
     * Sending the option value for a given checkout step.
     * @param action Action field object
     */
    checkoutOption(action) {
        window.dataLayer.push({
            event: "Elements@Ecommerce.CheckoutOption",
            ecommerce: {
                checkout_option: {
                    actionField: action,
                },
            },
        });
    }
    /**
     * The sale of one or more products.
     * @param product Product field object
     * @param transactionId The order transaction identifier
     * @param action Action field object
     */
    purchase(product, transactionId, action) {
        window.dataLayer.push({
            event: "Elements@Ecommerce.Purchase",
            ecommerce: {
                purchase: {
                    actionField: Object.assign(Object.assign({}, action), { id: transactionId }),
                    products: [product],
                },
            },
        });
        this.productFieldObject = product;
        return Ecommerce;
    }
    /**
     * The refund of one or more products.
     * @param product Product field object
     * @param transactionId The order transaction identifier
     * @param action Action field object
     */
    refund(product, transactionId, action) {
        window.dataLayer.push({
            event: "Elements@Ecommerce.Refund",
            ecommerce: {
                refund: {
                    actionField: Object.assign(Object.assign({}, action), { id: transactionId }),
                    products: [product],
                },
            },
        });
        this.productFieldObject = product;
        return Ecommerce;
    }
    /**
     * A click on an internal promotion.
     * @param promotion Promotion field object
     */
    promoClick(promotion) {
        window.dataLayer.push({
            event: "Elements@Ecommerce.PromoClick",
            ecommerce: {
                promoClick: {
                    promotions: [promotion],
                },
            },
        });
        this.promotionFieldObject = promotion;
        return Ecommerce;
    }
    /**
     * An impression of an internal promotion.
     * @param promotion Promotion field object
     */
    promoView(promotion) {
        window.dataLayer.push({
            event: "Elements@Ecommerce.PromoView",
            ecommerce: {
                promoView: {
                    promotions: [promotion],
                },
            },
        });
        this.promotionFieldObject = promotion;
        return Ecommerce;
    }
}

const CURRENT_PREMISE_STORAGE_LOCATION = "angular-cache.caches.vfAppCache.data.currentPremise";
const CONTRACT_HEADER_INFO_HEADER_MCO = "angular-cache.caches.vfAppCache.data.contractHeaderInfoMCO";
const CONTRACT_HEADER_INFO_PC = "angular-cache.caches.vfAppCache.data.contractInfoPC";
/**
 * This service is responsible for communicating with legacy components on the website
 * by directly reading and writing to the legacy storage interface – angular-cache.
 */
class LegacyAngularCacheService {
    constructor() { }
    /**
     * Gets an `currentPremise` object from legacy cache
     */
    getCurrentPremise() {
        const currentPremiseJson = window.localStorage.getItem(CURRENT_PREMISE_STORAGE_LOCATION);
        const currentPremise = currentPremiseJson ? JSON.parse(currentPremiseJson) : null;
        return currentPremise === null || currentPremise === void 0 ? void 0 : currentPremise.value;
    }
    /**
     * Creates and sets an `currentPremise` object to legacy cache
     */
    setCurrentPremise(activePremiseId) {
        window.localStorage.setItem(CURRENT_PREMISE_STORAGE_LOCATION, JSON.stringify({
            key: "currentPremise",
            value: activePremiseId,
        }));
    }
    /**
     * Creates and sets an `setContractHeaderPC` object to legacy cache
     */
    setContractHeaderPC(premiseId) {
        // Persist in local storage
        window.localStorage.setItem(CONTRACT_HEADER_INFO_PC, JSON.stringify({
            key: "contractInfoPC",
            value: {
                podId: premiseId,
            },
        }));
    }
    /**
     * Creates and sets an `contractHeaderInfoMCO` object to legacy cache
     */
    setContractHeaderInfoMCO({ electricityPriceTotalIncVat, lowElectricityPriceTotalIncVat, fixedFeeTotalIncVat, contractType, energyArea: currentContractEnergyArea, premiseId: podId, }) {
        var _a;
        // Calculate the total electrical price for the contract
        let currentContractTotalElectricalPrice = null;
        if (electricityPriceTotalIncVat === null || electricityPriceTotalIncVat === void 0 ? void 0 : electricityPriceTotalIncVat.value)
            currentContractTotalElectricalPrice = electricityPriceTotalIncVat.value * 0.01;
        else if (contractType === "TimeTariffPrice" && (lowElectricityPriceTotalIncVat === null || lowElectricityPriceTotalIncVat === void 0 ? void 0 : lowElectricityPriceTotalIncVat.value))
            currentContractTotalElectricalPrice = lowElectricityPriceTotalIncVat.value * 0.01;
        // Get the fixed fee total amount
        const currentContractFixedFee = (_a = fixedFeeTotalIncVat === null || fixedFeeTotalIncVat === void 0 ? void 0 : fixedFeeTotalIncVat.value) !== null && _a !== void 0 ? _a : null;
        // Persist in local storage
        window.localStorage.setItem(CONTRACT_HEADER_INFO_HEADER_MCO, JSON.stringify({
            key: "contractHeaderInfoMCO",
            value: {
                contract: {
                    podId,
                    currentContractTotalElectricalPrice,
                    currentContractFixedFee,
                    currentContractEnergyArea,
                },
            },
        }));
    }
}
LegacyAngularCacheService.ɵfac = function LegacyAngularCacheService_Factory(t) { return new (t || LegacyAngularCacheService)(); };
LegacyAngularCacheService.ɵprov = ɵɵdefineInjectable({ token: LegacyAngularCacheService, factory: LegacyAngularCacheService.ɵfac, providedIn: "root" });
/*@__PURE__*/ (function () { ɵsetClassMetadata(LegacyAngularCacheService, [{
        type: Injectable,
        args: [{
                providedIn: "root",
            }]
    }], function () { return []; }, null); })();

class GlobalStoreService {
    constructor() {
        this.$store = new ReplaySubject(0);
        this._globalStoreEvent = new Event("globalStore");
        this.$store.next(this.getGlobalStore());
        document.addEventListener("globalStore", () => {
            this.$store.next(this.getGlobalStore());
        });
    }
    get store() {
        return this.$store.asObservable();
    }
    dispatch(key, value) {
        this.$store.next(this.getGlobalStore());
        this.setGlobalStore(key, value);
        document.dispatchEvent(this._globalStoreEvent);
    }
    getGlobalStore() {
        var _a;
        return JSON.parse((_a = sessionStorage.getItem("VattenfallElementsGlobalStore")) !== null && _a !== void 0 ? _a : "{}");
    }
    setGlobalStore(key, value) {
        sessionStorage.setItem("VattenfallElementsGlobalStore", JSON.stringify(Object.assign(Object.assign({}, this.getGlobalStore()), { [key]: value })));
    }
}
GlobalStoreService.ɵfac = function GlobalStoreService_Factory(t) { return new (t || GlobalStoreService)(); };
GlobalStoreService.ɵprov = ɵɵdefineInjectable({ token: GlobalStoreService, factory: GlobalStoreService.ɵfac, providedIn: "root" });
/*@__PURE__*/ (function () { ɵsetClassMetadata(GlobalStoreService, [{
        type: Injectable,
        args: [{
                providedIn: "root",
            }]
    }], function () { return []; }, null); })();

/**
 * Global map to hold all slot elements as observables, the host element is the namespace of the shadow subtree that the slot element is attached to, and the nested map contains a reference to the original slot element's slotchange event target element.
 */
const slotMap = new Map();
/**
 * This directive creates copies of a slot's assigned elements and inserts those after all other slot element with the same reference.
 *
 * By design, the Shadow DOM will only insert an assigned element once and then destroys all other slot elements when the first instance has been determent. This is a problem when repeating a slot, for example in an `*ngFor` loop where only the first instance will be assigned. This directive solves this issue.
 *
 * **isStatic**
 *
 * For performance reasons, slot elements are only assigned once by default. However, if an element needs to be updated after the initial assignment you can make it none-static, meaning that the slots will be reassigned on every `slotchange` event.
 *
 * To update the slot on every `slotchange` event, set the `isStatic` property to false.
 *
 * ```html
 * <slot [isStatic]="false">
 * ```
 */
class SlotDirective {
    constructor(elementRef, sanitizer) {
        this.elementRef = elementRef;
        this.sanitizer = sanitizer;
        /**
         * Value of the slot element's `name` attribute. Empty values are possible.
         */
        this.name = "";
        /**
         * Set to `false` if slot value is expected to change after first render. Default is `true` for performance reasons.
         */
        this.isStatic = true;
    }
    ngAfterViewInit() {
        this.slotchangeSubscription = this.onSlotchange().subscribe((slotElement) => {
            // Ignore the original slot element
            if (!slotElement.isSameNode(this.elementRef.nativeElement)) {
                // Clear the native element of its previous value
                this.elementRef.nativeElement.innerHTML = "";
                // Iterate each assigned slottable element clone
                for (const assignedNode of slotElement.assignedNodes().map((assignedNode) => assignedNode.cloneNode(true))) {
                    // Sanitize the projected markup as HTML elements or text nodes
                    if (assignedNode instanceof Element) {
                        // Remove the slot attribute to avoid a DOM sanitizer warnings
                        assignedNode.removeAttribute("slot");
                        this.elementRef.nativeElement.innerHTML += this.sanitizer.sanitize(SecurityContext.HTML, assignedNode.outerHTML);
                    }
                    if (assignedNode instanceof Text)
                        this.elementRef.nativeElement.innerHTML += this.sanitizer.sanitize(SecurityContext.HTML, assignedNode.textContent);
                }
            }
            // Kill the subscription immediately after the initial slot duplication for static slot elements,
            // placed last in callstack to make the event stream asynchronous so that it can be unsubscribed
            // from within the subscription
            setTimeout(() => {
                if (this.isStatic)
                    this.ngOnDestroy();
            });
        });
    }
    ngOnDestroy() {
        if (!this.slotchangeSubscription.closed)
            this.slotchangeSubscription.unsubscribe();
    }
    /**
     * Returns an observable that emits on to the original slot element's slotchange event and that is scoped to the shadow root subtree that this slot element is attached to.
     */
    onSlotchange() {
        var _a, _b, _c;
        // The host element of the shadow subtree that this slot element belongs to
        const hostElement = this.elementRef.nativeElement.getRootNode().host;
        // This is the first slot element of this host
        if (!slotMap.get(hostElement))
            slotMap.set(hostElement, new Map([[this.name, this.getSlotElementSlotchangeMutationObservable()]]));
        // This is the first slot in the template markup with this this name
        else if (!((_a = slotMap.get(hostElement)) === null || _a === void 0 ? void 0 : _a.has(this.name)))
            (_b = slotMap.get(hostElement)) === null || _b === void 0 ? void 0 : _b.set(this.name, this.getSlotElementSlotchangeMutationObservable());
        // Return observable for this slot element scoped to the host element namespace
        return (_c = slotMap.get(hostElement)) === null || _c === void 0 ? void 0 : _c.get(this.name);
    }
    /**
     * Returns an observable for the current slot element that emits when new slots elements are created or removed or a change is observed from the slottable element and/or its children.
     */
    getSlotElementSlotchangeMutationObservable() {
        // Emits on changes to the slottable element and/or its children
        const slotMutationObservable = new Observable((observer) => {
            const mutation = new MutationObserver(() => {
                observer.next(this.elementRef.nativeElement);
            });
            for (const assignedNode of this.elementRef.nativeElement.assignedNodes())
                mutation.observe(assignedNode, {
                    attributes: true,
                    childList: true,
                    subtree: true,
                    characterData: true,
                });
            const unsubscribe = () => {
                mutation.disconnect();
            };
            return unsubscribe;
        });
        // Emits when slot elements are added/removed in the template markup
        // or when the slottable element attributes change
        const slotchangeObservable = fromEvent(this.elementRef.nativeElement, "slotchange");
        // Merge slotchange event and mutation observer into a single event stream
        return merge(slotMutationObservable, slotchangeObservable).pipe(
        // Start emitting stream using the original slot element
        startWith(this.elementRef.nativeElement), 
        // Pluck the event target element on subsequent slotchange events
        map((event) => ("target" in event ? event.target : event)), 
        // Ensure that the slot element has assigned nodes before proceeding
        filter((slotElement) => slotElement.assignedNodes().length !== 0));
    }
}
SlotDirective.ɵfac = function SlotDirective_Factory(t) { return new (t || SlotDirective)(ɵɵdirectiveInject(ElementRef), ɵɵdirectiveInject(DomSanitizer)); };
SlotDirective.ɵdir = ɵɵdefineDirective({ type: SlotDirective, selectors: [["slot"]], inputs: { name: "name", isStatic: "isStatic" } });
/*@__PURE__*/ (function () { ɵsetClassMetadata(SlotDirective, [{
        type: Directive,
        args: [{
                selector: "slot",
            }]
    }], function () { return [{ type: ElementRef }, { type: DomSanitizer }]; }, { name: [{
            type: Input
        }], isStatic: [{
            type: Input
        }] }); })();

class SlotModule {
}
SlotModule.ɵmod = ɵɵdefineNgModule({ type: SlotModule });
SlotModule.ɵinj = ɵɵdefineInjector({ factory: function SlotModule_Factory(t) { return new (t || SlotModule)(); }, imports: [[CommonModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵɵsetNgModuleScope(SlotModule, { declarations: [SlotDirective], imports: [CommonModule], exports: [SlotDirective] }); })();
/*@__PURE__*/ (function () { ɵsetClassMetadata(SlotModule, [{
        type: NgModule,
        args: [{
                declarations: [SlotDirective],
                imports: [CommonModule],
                exports: [SlotDirective],
            }]
    }], null, null); })();

/**
 * Check if value pass to formControl is empty or null.
 *
 */
function isEmptyInputValue(value) {
    return value == null || value.length === 0;
}
/**
 * Provides a set of custom built Vattenfall Elements validators that can be used by form controls.
 *
 * A validator is a function that processes a `FormControl` or collection of
 * controls and returns an error map or null. A null map means that validation has passed.
 *
 */
class ElementsValidators {
    /**
     * Validator that requires the control's value pass a email address and tests that it is properly formated and has an offical domain extention.
     *
     * @param region specifies region (`se`) or (`fi`) for which validation is supported
     *
     * ### Validate that the field matches a valid email address pattern
     *
     * ```typescript
     * const control = new FormControl('firstname.lastname@gmail.com', ElementsValidators.emailAddress("se"));
     *
     * const control = new FormControl('förstname.lästname@gmail.com', ElementsValidators.emailAddress("fi"));
     *
     * console.log(control.errors); // { emailAddress: true } or throw error on non-supported region
     * ```
     *
     * @returns An error map with the `emailAddress` property
     * if the validation check fails, otherwise `null`.
     */
    static emailAddress(region) {
        return (control) => {
            if (isEmptyInputValue(control.value))
                return null;
            switch (region) {
                case "fi":
                    return isValidFinnishEmailAddress(control.value) ? null : { emailAddress: true };
                case "se":
                    return isValidSwedishEmailAddress(control.value) ? null : { emailAddress: true };
                default:
                    throw Error(`[ Vattenfall Elements ] The provided region '${region}' is not supported by the validator 'emailAddress'.`);
            }
        };
    }
    /**
     * Validator that requires the control's value pass an swedish organisation number/finland business-id regex test and algorithm.
     *
     * @param region specifies region (`fi`) or (`se`) for which validation is supported
     *
     * ### Validate that the field matches a valid organisationNumber pattern
     *
     * ```typescript
     * const control = new FormControl('556036-2138', ElementsValidators.organisationNumber("se"));
     *
     * console.log(control.errors); // { organisationNumber: true } or throw error on non-supported region
     * ```
     *
     * @returns An error map with the `organisationNumber` property
     * if the validation check fails, otherwise `null`.
     */
    static organisationNumber(region) {
        return (control) => {
            if (isEmptyInputValue(control.value))
                return null;
            switch (region) {
                case "fi":
                    return isValidFinnishBusinessNumber(control.value) ? null : { organisationNumber: true };
                case "se":
                    return isValidSwedishBusinessNumber(control.value) ? null : { organisationNumber: true };
                default:
                    throw Error(`[ Vattenfall Elements ] The provided region '${region}' is not supported by the validator 'organisationNumber'.`);
            }
        };
    }
    /**
     * Validator that requires the control's value pass an swedish/finnish personal number regex test and algorithm.
     *
     * @param region specifies region (`fi`)and (`se`) for which validation pattern is supported
     * @param minAge specifies legal minimum age to validate social security number
     * @param maxAge specifies legal maximum age to validate social security number
     *
     * ### Validate that the field matches a valid personal number pattern and algorithm
     *
     * ```typescript
     * const control = new FormControl('220556-029L', ElementsValidators.personalNumber("fi"));
     *
     * const control = new FormControl('640823-3234', ElementsValidators.personalNumber("se"));
     *
     * console.log(control.errors); // { personalNumber: true } or throw error on non-supported region
     * ```
     *
     * @returns An error map with the `personalNumber` property
     * if the validation check fails, otherwise `null`.
     */
    static personalNumber(region, minAge, maxAge) {
        return (control) => {
            if (isEmptyInputValue(control.value))
                return null;
            switch (region) {
                case "fi":
                    if (isValidFinnishPersonalNumber(control.value))
                        if (isValidAgeRange(control.value, region, minAge, maxAge))
                            return null;
                        else
                            return { personalNumberAge: true };
                    else
                        return { personalNumber: true };
                case "se":
                    if (isValidSwedishPersonalNumber(control.value))
                        if (isValidAgeRange(control.value, region, minAge, maxAge))
                            return null;
                        else
                            return { personalNumberAge: true };
                    else
                        return { personalNumber: true };
                default:
                    throw Error(`[ Vattenfall Elements ] The provided region '${region}' is not supported by the validator 'personalNumber'.`);
            }
        };
    }
    /**
     * Validator that requires the control's value pass an swedish/finnish phone number regex test and algorithm.
     *
     * @param region specifies region (`fi`)and (`se`) for which validation pattern is supported
     *
     * ### Validate that the field matches a valid phone number pattern and algorithm
     *
     * ```typescript
     * const control = new FormControl('+358406361091', ElementsValidators.phoneNumber("fi"));
     *
     * const control = new FormControl('+4620-820000', ElementsValidators.phoneNumber("se"));
     *
     * console.log(control.errors); // { phoneNumber: true } or throw error on non-supported region
     * ```
     *
     * @returns An error map with the `phoneNumber` property
     * if the validation check fails, otherwise `null`.
     */
    static phoneNumber(region) {
        return (control) => {
            if (isEmptyInputValue(control.value))
                return null;
            switch (region) {
                case "fi":
                    return isValidFinnishPhoneNumber(control.value) ? null : { phoneNumber: true };
                case "se":
                    return isValidSwedishPhoneNumber(control.value) ? null : { phoneNumber: true };
                default:
                    throw Error(`[ Vattenfall Elements ] The provided region '${region}' is not supported by the validator 'phoneNumber'.`);
            }
        };
    }
    /**
     * Validator that requires the control's value pass an swedish premise id validation test.
     *
     * @param region specifies region (`se`) for which validation pattern is supported
     *
     * ### Validate that the field matches a valid premise id pattern
     *
     * ```typescript
     * const control = new FormControl('735999100054384456', ElementsValidators.premiseId("se"));
     *
     * console.log(control.errors); // { premiseId: true } or throw error on non-supported region
     * ```
     *
     * @returns An error map with the `premiseId` property
     * if the validation check fails, otherwise `null`.
     */
    static premiseId(region) {
        return (control) => {
            if (isEmptyInputValue(control.value))
                return null;
            switch (region) {
                case "se":
                    return isValidSwedishPremiseId(control.value) ? null : { premiseId: true };
                default:
                    throw Error(`[ Vattenfall Elements ] The provided region '${region}' is not supported by the validator 'premiseId'.`);
            }
        };
    }
    /**
     * Validator that requires the control's value pass an swedish/finnish postal code regex test and algorithm.
     *
     * @param region specifies region (`fi`), (`se`), and (`intl`) for which validation pattern is supported
     *
     * ### Validate that the field matches a valid postal code pattern and algorithm
     *
     * ```typescript
     * const control = new FormControl('62100', ElementsValidators.postalCode("fi"));
     *
     * const control = new FormControl('16956', ElementsValidators.postalCode("se"));
     *
     *const control = new FormControl('16956', ElementsValidators.postalCode("intl"));'
  
     * console.log(control.errors); // { postalCode: true } or throw error on non-supported region
     * ```
     *
     * @returns An error map with the `postalCode` property
     * if the validation check fails, otherwise `null`.
     *
     *
     */
    static postalCode(region) {
        return (control) => {
            if (isEmptyInputValue(control.value))
                return null;
            switch (region) {
                case "fi":
                    return isValidFinnishPostalCode(control.value) ? null : { postalCode: true };
                case "se":
                    return isValidSwedishPostalCode(control.value) ? null : { postalCode: true };
                case "intl":
                    return isValidInternationalPostalCode(control.value) ? null : { postalCode: true };
                default:
                    throw Error(`[ Vattenfall Elements ] The provided region '${region}' is not supported by the validator 'postalCode'.`);
            }
        };
    }
    /**
     * Validator that requires the control's value pass the regexp for apartment number validation.
     *
     * ```typescript
     * const control = new FormControl('1234', ElementsValidators.apartmentNumber())
     * console.log(control.errors); {apartmentNumber: true}
     * ```
     */
    static apartmentNumber() {
        return (control) => {
            if (isEmptyInputValue(control.value))
                return null;
            return isValidSwedishApartmentNumber(control.value) ? null : { apartmentNumber: true };
        };
    }
    /**
     * Validator that requires the control's value pass the regexp for postal town validation.
     *
     * ```typescript
     * const control = new FormControl('Örebro', ElementsValidators.postalTown())
     *
     * console.log(control.errors); // { postalTown: true } or throw error on non-supported region
     * ```
     */
    static postalTown() {
        return (control) => {
            if (isEmptyInputValue(control.value))
                return null;
            return isValidSwedishFinnishPostalTown(control.value) ? null : { postalTown: true };
        };
    }
    /**
     * Validator that requires the control's value pass the regexp for passwordvalidation.
     *
     * ```typescript
     * const control = new FormControl('azAZ09!@#$%^&*', ElementsValidators.password())
     * console.log(control.errors); {password: true}
     * ```
     */
    static password() {
        // TODO (stefan): This needs to be locale sensitive
        return (control) => {
            if (isEmptyInputValue(control.value))
                return null;
            return isValidPassword(control.value) ? null : { password: true };
        };
    }
    /**
     * Validator that requires the control's value pass an swedish grid area id.
     *
     * ```typescript
     * const control = new FormControl('STH', ElementsValidators.gridAreaId())
     * console.log(control.errors); {gridAreaId: true}
     * ```
     */
    static gridAreaId() {
        // TODO (stefan): This needs to be locale sensitive
        return (control) => {
            if (isEmptyInputValue(control.value))
                return null;
            return isValidGridAreaId(control.value) ? null : { gridAreaId: true };
        };
    }
    /**
     * Validator that requires the control's value pass a name validation test.
     *
     * ### Validate that the field matches a valid personal name pattern
     *
     * ```typescript
     * const control = new FormControl('John', ElementsValidators.personalName());
     *
     * console.log(control.errors); // { personalName: true }
     * ```
     *
     * @returns An error map with the `personalName` property
     * if the validation check fails, otherwise `null`.
     */
    static personalName() {
        return (control) => {
            if (isEmptyInputValue(control.value))
                return null;
            return isValidPersonalName(control.value) ? null : { personalName: true };
        };
    }
    /**
     * Validator that requires the control's value pass the regexp for street name validation.
     *
     * for premise, const control = new FormControl('a-zA-Z0-9åäöÅÄÖ', ElementsValidators.streetName())
     * for invoice, const control = new FormControl('a-zA-Z0-9åäöÅÄÖÆæØøÜüß', ElementsValidators.streetName())
     * console.log(control.errors); {streetName: true}
     * ```
     */
    static streetName(context) {
        return (control) => {
            if (isEmptyInputValue(control.value))
                return null;
            return isValidStreetName(control.value, context) ? null : { streetName: true };
        };
    }
    /**
     * Validator that requires the control's value pass the regexp for care of address.
     *
     * const control = new FormControl('azAZ099åäöÅÄÖÆæØøÜüß ElementsValidators.careOfAddress())
     * console.log(control.errors); {careOfAddress: true}
     */
    static careOfAddress() {
        return (control) => {
            return isValidCareOfAddress(control.value) ? null : { careOfAddress: true };
        };
    }
}

// TODO:
/**
 * A base class for validator-based directives.
 */
class AbstractValidatorDirective {
    constructor() {
        /**
         * Initialize initial state of validator to nullValidator.
         */
        this._validator = Validators.nullValidator;
    }
    /**
     * Helper function invoked from child classes to process changes (from `ngOnChanges` hook)
     */
    handleChanges(changes) {
        if (this.inputName in changes) {
            const input = this.normalizeInput(changes[this.inputName].currentValue);
            this._validator = this.createValidator(input);
            if (this._onChange) {
                this._onChange();
            }
        }
    }
    validate(control) {
        return this._validator(control);
    }
    registerOnValidatorChange(fn) {
        this._onChange = fn;
    }
}
/**
 * A directive which installs the EmailValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `emailAddress` attribute.
 *
 * ### Adding a emailAddress validator
 *
 * The following example shows how to add a organisationNumber validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel emailAddress>
 * ```
 */
class EmailAddressValidator extends AbstractValidatorDirective {
    constructor(environment) {
        super();
        this.environment = environment;
        this.inputName = "emailAddress";
        this.normalizeInput = (input) => input;
        this.createValidator = () => ElementsValidators.emailAddress(this.environment.region);
    }
    /**
     * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
     * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
     * AOT mode. This could be refactored once ViewEngine is removed.
     */
    ngOnChanges(changes) {
        this.handleChanges(changes);
    }
}
EmailAddressValidator.ɵfac = function EmailAddressValidator_Factory(t) { return new (t || EmailAddressValidator)(ɵɵdirectiveInject(ENVIRONMENT, 8)); };
EmailAddressValidator.ɵdir = ɵɵdefineDirective({ type: EmailAddressValidator, selectors: [["", "emailAddress", "", "formControlName", ""], ["", "emailAddress", "", "formControl", ""], ["", "emailAddress", "", "ngModel", ""]], hostVars: 1, hostBindings: function EmailAddressValidator_HostBindings(rf, ctx) { if (rf & 2) {
        ɵɵattribute("emailAddress", ctx.emailAddress ? ctx.emailAddress : null);
    } }, inputs: { emailAddress: "emailAddress" }, features: [ɵɵProvidersFeature([{ provide: NG_VALIDATORS, useExisting: forwardRef(() => EmailAddressValidator), multi: true }]), ɵɵInheritDefinitionFeature, ɵɵNgOnChangesFeature] });
/*@__PURE__*/ (function () { ɵsetClassMetadata(EmailAddressValidator, [{
        type: Directive,
        args: [{
                selector: "[emailAddress][formControlName],[emailAddress][formControl],[emailAddress][ngModel]",
                providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => EmailAddressValidator), multi: true }],
                host: { "[attr.emailAddress]": "emailAddress ? emailAddress : null" },
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [ENVIRONMENT]
            }] }]; }, { emailAddress: [{
            type: Input
        }] }); })();
/**
 * A directive which installs the OrganisationNumberValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `organisationNumber` attribute.
 *
 * ### Adding a organisationNumber validator
 *
 * The following example shows how to add a organisationNumber validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel organisationNumber>
 * ```
 */
class OrganisationNumberValidator extends AbstractValidatorDirective {
    constructor(environment) {
        super();
        this.environment = environment;
        this.inputName = "organisationNumber";
        this.normalizeInput = (input) => input;
        this.createValidator = () => ElementsValidators.organisationNumber(this.environment.region);
    }
    /**
     * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
     * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
     * AOT mode. This could be refactored once ViewEngine is removed.
     */
    ngOnChanges(changes) {
        this.handleChanges(changes);
    }
}
OrganisationNumberValidator.ɵfac = function OrganisationNumberValidator_Factory(t) { return new (t || OrganisationNumberValidator)(ɵɵdirectiveInject(ENVIRONMENT, 8)); };
OrganisationNumberValidator.ɵdir = ɵɵdefineDirective({ type: OrganisationNumberValidator, selectors: [["", "organisationNumber", "", "formControlName", ""], ["", "organisationNumber", "", "formControl", ""], ["", "organisationNumber", "", "ngModel", ""]], hostVars: 1, hostBindings: function OrganisationNumberValidator_HostBindings(rf, ctx) { if (rf & 2) {
        ɵɵattribute("organisationNumber", ctx.organisationNumber ? ctx.organisationNumber : null);
    } }, inputs: { organisationNumber: "organisationNumber" }, features: [ɵɵProvidersFeature([{ provide: NG_VALIDATORS, useExisting: forwardRef(() => OrganisationNumberValidator), multi: true }]), ɵɵInheritDefinitionFeature, ɵɵNgOnChangesFeature] });
/*@__PURE__*/ (function () { ɵsetClassMetadata(OrganisationNumberValidator, [{
        type: Directive,
        args: [{
                selector: "[organisationNumber][formControlName],[organisationNumber][formControl],[organisationNumber][ngModel]",
                providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => OrganisationNumberValidator), multi: true }],
                host: { "[attr.organisationNumber]": "organisationNumber ? organisationNumber : null" },
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [ENVIRONMENT]
            }] }]; }, { organisationNumber: [{
            type: Input
        }] }); })();
/**
 * A directive which installs the PersonalNumberValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `personalNumber` attribute.
 *
 * ### Adding a personalNumber validator
 *
 * The following example shows how to add a personalNumber validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel personalNumber>
 * ```
 *
 * **Specifing a min and max age range**
 *
 * ```html
 * <ui-input variant="text" ngModel personalNumber minAge="18" maxAge="100">
 * ```
 */
class PersonalNumberValidator extends AbstractValidatorDirective {
    constructor(environment) {
        super();
        this.environment = environment;
        this.inputName = "personalNumber";
        this.normalizeInput = (input) => input;
        this.createValidator = () => ElementsValidators.personalNumber(this.environment.region, this.minAge, this.maxAge);
    }
    /**
     * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
     * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
     * AOT mode. This could be refactored once ViewEngine is removed.
     */
    ngOnChanges(changes) {
        this.handleChanges(changes);
    }
}
PersonalNumberValidator.ɵfac = function PersonalNumberValidator_Factory(t) { return new (t || PersonalNumberValidator)(ɵɵdirectiveInject(ENVIRONMENT, 8)); };
PersonalNumberValidator.ɵdir = ɵɵdefineDirective({ type: PersonalNumberValidator, selectors: [["", "personalNumber", "", "formControlName", ""], ["", "personalNumber", "", "formControl", ""], ["", "personalNumber", "", "ngModel", ""]], hostVars: 1, hostBindings: function PersonalNumberValidator_HostBindings(rf, ctx) { if (rf & 2) {
        ɵɵattribute("personalNumber", ctx.personalNumber ? ctx.personalNumber : null);
    } }, inputs: { personalNumber: "personalNumber", minAge: "minAge", maxAge: "maxAge" }, features: [ɵɵProvidersFeature([{ provide: NG_VALIDATORS, useExisting: forwardRef(() => PersonalNumberValidator), multi: true }]), ɵɵInheritDefinitionFeature, ɵɵNgOnChangesFeature] });
/*@__PURE__*/ (function () { ɵsetClassMetadata(PersonalNumberValidator, [{
        type: Directive,
        args: [{
                selector: "[personalNumber][formControlName],[personalNumber][formControl],[personalNumber][ngModel]",
                providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => PersonalNumberValidator), multi: true }],
                host: { "[attr.personalNumber]": "personalNumber ? personalNumber : null" },
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [ENVIRONMENT]
            }] }]; }, { personalNumber: [{
            type: Input
        }], minAge: [{
            type: Input
        }], maxAge: [{
            type: Input
        }] }); })();
/**
 * A directive which installs the PhoneNumberValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `phoneNumber` attribute.
 *
 * ### Adding a phoneNumber validator
 *
 * The following example shows how to add a phoneNumber validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel phoneNumber>
 * ```
 */
class PhoneNumberValidator extends AbstractValidatorDirective {
    constructor(environment) {
        super();
        this.environment = environment;
        this.inputName = "phoneNumber";
        this.normalizeInput = (input) => input;
        this.createValidator = () => ElementsValidators.phoneNumber(this.environment.region);
    }
    /**
     * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
     * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
     * AOT mode. This could be refactored once ViewEngine is removed.
     */
    ngOnChanges(changes) {
        this.handleChanges(changes);
    }
}
PhoneNumberValidator.ɵfac = function PhoneNumberValidator_Factory(t) { return new (t || PhoneNumberValidator)(ɵɵdirectiveInject(ENVIRONMENT, 8)); };
PhoneNumberValidator.ɵdir = ɵɵdefineDirective({ type: PhoneNumberValidator, selectors: [["", "phoneNumber", "", "formControlName", ""], ["", "phoneNumber", "", "formControl", ""], ["", "phoneNumber", "", "ngModel", ""]], hostVars: 1, hostBindings: function PhoneNumberValidator_HostBindings(rf, ctx) { if (rf & 2) {
        ɵɵattribute("phoneNumber", ctx.phoneNumber ? ctx.phoneNumber : null);
    } }, inputs: { phoneNumber: "phoneNumber" }, features: [ɵɵProvidersFeature([{ provide: NG_VALIDATORS, useExisting: forwardRef(() => PhoneNumberValidator), multi: true }]), ɵɵInheritDefinitionFeature, ɵɵNgOnChangesFeature] });
/*@__PURE__*/ (function () { ɵsetClassMetadata(PhoneNumberValidator, [{
        type: Directive,
        args: [{
                selector: "[phoneNumber][formControlName],[phoneNumber][formControl],[phoneNumber][ngModel]",
                providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => PhoneNumberValidator), multi: true }],
                host: { "[attr.phoneNumber]": "phoneNumber ? phoneNumber : null" },
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [ENVIRONMENT]
            }] }]; }, { phoneNumber: [{
            type: Input
        }] }); })();
/**
 * A directive which installs the PremiseIdValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `premiseId` attribute.
 *
 * ### Adding a premiseId validator
 *
 * The following example shows how to add a premiseId validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel premiseId>
 * ```
 */
class PremiseIdValidator extends AbstractValidatorDirective {
    constructor(environment) {
        super();
        this.environment = environment;
        this.inputName = "premiseId";
        this.normalizeInput = (input) => input;
        this.createValidator = () => ElementsValidators.premiseId(this.environment.region);
    }
    /**
     * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
     * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
     * AOT mode. This could be refactored once ViewEngine is removed.
     */
    ngOnChanges(changes) {
        this.handleChanges(changes);
    }
}
PremiseIdValidator.ɵfac = function PremiseIdValidator_Factory(t) { return new (t || PremiseIdValidator)(ɵɵdirectiveInject(ENVIRONMENT, 8)); };
PremiseIdValidator.ɵdir = ɵɵdefineDirective({ type: PremiseIdValidator, selectors: [["", "premiseId", "", "formControlName", ""], ["", "premiseId", "", "formControl", ""], ["", "premiseId", "", "ngModel", ""]], hostVars: 1, hostBindings: function PremiseIdValidator_HostBindings(rf, ctx) { if (rf & 2) {
        ɵɵattribute("premiseId", ctx.premiseId ? ctx.premiseId : null);
    } }, inputs: { premiseId: "premiseId" }, features: [ɵɵProvidersFeature([{ provide: NG_VALIDATORS, useExisting: forwardRef(() => PremiseIdValidator), multi: true }]), ɵɵInheritDefinitionFeature, ɵɵNgOnChangesFeature] });
/*@__PURE__*/ (function () { ɵsetClassMetadata(PremiseIdValidator, [{
        type: Directive,
        args: [{
                selector: "[premiseId][formControlName],[premiseId][formControl],[premiseId][ngModel]",
                providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => PremiseIdValidator), multi: true }],
                host: { "[attr.premiseId]": "premiseId ? premiseId : null" },
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [ENVIRONMENT]
            }] }]; }, { premiseId: [{
            type: Input
        }] }); })();
/**
 * A directive which installs the PostalCodeValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `postalCode` attribute.
 *
 * ### Adding a postalCode validator
 *
 * The following example shows how to add a postalCode validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel postalCode>
 * ```
 */
class PostalCodeValidator extends AbstractValidatorDirective {
    constructor(environment) {
        super();
        this.environment = environment;
        this.inputName = "postalCode";
        this.normalizeInput = (input) => input;
        this.createValidator = () => ElementsValidators.postalCode(this.environment.region);
    }
    /**
     * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
     * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
     * AOT mode. This could be refactored once ViewEngine is removed.
     */
    ngOnChanges(changes) {
        this.handleChanges(changes);
    }
}
PostalCodeValidator.ɵfac = function PostalCodeValidator_Factory(t) { return new (t || PostalCodeValidator)(ɵɵdirectiveInject(ENVIRONMENT, 8)); };
PostalCodeValidator.ɵdir = ɵɵdefineDirective({ type: PostalCodeValidator, selectors: [["", "postalCode", "", "formControlName", ""], ["", "postalCode", "", "formControl", ""], ["", "postalCode", "", "ngModel", ""]], hostVars: 1, hostBindings: function PostalCodeValidator_HostBindings(rf, ctx) { if (rf & 2) {
        ɵɵattribute("postalCode", ctx.postalCode ? ctx.postalCode : null);
    } }, inputs: { postalCode: "postalCode" }, features: [ɵɵProvidersFeature([{ provide: NG_VALIDATORS, useExisting: forwardRef(() => PostalCodeValidator), multi: true }]), ɵɵInheritDefinitionFeature, ɵɵNgOnChangesFeature] });
/*@__PURE__*/ (function () { ɵsetClassMetadata(PostalCodeValidator, [{
        type: Directive,
        args: [{
                selector: "[postalCode][formControlName],[postalCode][formControl],[postalCode][ngModel]",
                providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => PostalCodeValidator), multi: true }],
                host: { "[attr.postalCode]": "postalCode ? postalCode : null" },
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [ENVIRONMENT]
            }] }]; }, { postalCode: [{
            type: Input
        }] }); })();
/**
 * A directive which installs the Passwordfor any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `password` attribute.
 *
 * ### Adding a password validator
 *
 * The following example shows how to add a password validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel password>
 * ```
 */
class PasswordValidator extends AbstractValidatorDirective {
    constructor() {
        super();
        this.inputName = "password";
        this.normalizeInput = (input) => input;
        this.createValidator = () => ElementsValidators.password();
    }
    /**
     * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
     * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
     * AOT mode. This could be refactored once ViewEngine is removed.
     */
    ngOnChanges(changes) {
        this.handleChanges(changes);
    }
}
PasswordValidator.ɵfac = function PasswordValidator_Factory(t) { return new (t || PasswordValidator)(); };
PasswordValidator.ɵdir = ɵɵdefineDirective({ type: PasswordValidator, selectors: [["", "password", "", "formControlName", ""], ["", "password", "", "formControl", ""], ["", "password", "", "ngModel", ""]], hostVars: 1, hostBindings: function PasswordValidator_HostBindings(rf, ctx) { if (rf & 2) {
        ɵɵattribute("password", ctx.password ? ctx.password : null);
    } }, inputs: { password: "password" }, features: [ɵɵProvidersFeature([{ provide: NG_VALIDATORS, useExisting: forwardRef(() => PasswordValidator), multi: true }]), ɵɵInheritDefinitionFeature, ɵɵNgOnChangesFeature] });
/*@__PURE__*/ (function () { ɵsetClassMetadata(PasswordValidator, [{
        type: Directive,
        args: [{
                selector: "[password][formControlName],[password][formControl],[password][ngModel]",
                providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => PasswordValidator), multi: true }],
                host: { "[attr.password]": "password ? password : null" },
            }]
    }], function () { return []; }, { password: [{
            type: Input
        }] }); })();
/**
 * A directive which installs the MinValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `min` attribute.
 *
 * ### Adding a min validator
 *
 * The following example shows how to add a min validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel min="2">
 * ```
 */
class MinValidator extends AbstractValidatorDirective {
    constructor() {
        super(...arguments);
        this.inputName = "min";
        this.normalizeInput = (input) => parseInt(input, 10);
        this.createValidator = (min) => Validators.min(min);
    }
    /**
     * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
     * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
     * AOT mode. This could be refactored once ViewEngine is removed.
     */
    ngOnChanges(changes) {
        this.handleChanges(changes);
    }
}
MinValidator.ɵfac = function MinValidator_Factory(t) { return ɵMinValidator_BaseFactory(t || MinValidator); };
MinValidator.ɵdir = ɵɵdefineDirective({ type: MinValidator, selectors: [["", "min", "", "formControlName", ""], ["", "min", "", "formControl", ""], ["", "min", "", "ngModel", ""]], hostVars: 1, hostBindings: function MinValidator_HostBindings(rf, ctx) { if (rf & 2) {
        ɵɵattribute("min", ctx.min ? ctx.min : null);
    } }, inputs: { min: "min" }, features: [ɵɵProvidersFeature([{ provide: NG_VALIDATORS, useExisting: forwardRef(() => MinValidator), multi: true }]), ɵɵInheritDefinitionFeature, ɵɵNgOnChangesFeature] });
const ɵMinValidator_BaseFactory = ɵɵgetInheritedFactory(MinValidator);
/*@__PURE__*/ (function () { ɵsetClassMetadata(MinValidator, [{
        type: Directive,
        args: [{
                selector: "[min][formControlName],[min][formControl],[min][ngModel]",
                providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => MinValidator), multi: true }],
                host: { "[attr.min]": "min ? min : null" },
            }]
    }], null, { min: [{
            type: Input
        }] }); })();
/**
 * A directive which installs the MaxValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `max` attribute.
 *
 * ### Adding a max validator
 *
 * The following example shows how to add a max validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel max="10">
 * ```
 */
class MaxValidator extends AbstractValidatorDirective {
    constructor() {
        super(...arguments);
        this.inputName = "max";
        this.normalizeInput = (input) => parseInt(input, 10);
        this.createValidator = (max) => Validators.max(max);
    }
    /**
     * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
     * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
     * AOT mode. This could be refactored once ViewEngine is removed.
     */
    ngOnChanges(changes) {
        this.handleChanges(changes);
    }
}
MaxValidator.ɵfac = function MaxValidator_Factory(t) { return ɵMaxValidator_BaseFactory(t || MaxValidator); };
MaxValidator.ɵdir = ɵɵdefineDirective({ type: MaxValidator, selectors: [["", "max", "", "formControlName", ""], ["", "max", "", "formControl", ""], ["", "max", "", "ngModel", ""]], hostVars: 1, hostBindings: function MaxValidator_HostBindings(rf, ctx) { if (rf & 2) {
        ɵɵattribute("max", ctx.max ? ctx.max : null);
    } }, inputs: { max: "max" }, features: [ɵɵProvidersFeature([{ provide: NG_VALIDATORS, useExisting: forwardRef(() => MaxValidator), multi: true }]), ɵɵInheritDefinitionFeature, ɵɵNgOnChangesFeature] });
const ɵMaxValidator_BaseFactory = ɵɵgetInheritedFactory(MaxValidator);
/*@__PURE__*/ (function () { ɵsetClassMetadata(MaxValidator, [{
        type: Directive,
        args: [{
                selector: "[max][formControlName],[max][formControl],[max][ngModel]",
                providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => MaxValidator), multi: true }],
                host: { "[attr.max]": "max ? max : null" },
            }]
    }], null, { max: [{
            type: Input
        }] }); })();
/**
 * A directive which installs the GridAreaIdValidator for any `formControlName`,
 * `formControl`, or control with `ngModel` that also has a `gridAreaId` attribute.
 *
 * ### Adding a gridAreaId validator
 *
 * The following example shows how to add a gridAreaId validator to an ui-input attached to an
 * ngModel binding.
 *
 * ```html
 * <ui-input variant="text" ngModel gridAreaId>
 * ```
 */
class GridAreaIdValidator extends AbstractValidatorDirective {
    constructor() {
        super(...arguments);
        this.inputName = "gridAreaId";
        this.normalizeInput = (input) => input;
        this.createValidator = () => ElementsValidators.gridAreaId();
    }
    /**
     * Declare `ngOnChanges` lifecycle hook at the main directive level (vs keeping it in base class)
     * to avoid differences in handling inheritance of lifecycle hooks between Ivy and ViewEngine in
     * AOT mode. This could be refactored once ViewEngine is removed.
     */
    ngOnChanges(changes) {
        this.handleChanges(changes);
    }
}
GridAreaIdValidator.ɵfac = function GridAreaIdValidator_Factory(t) { return ɵGridAreaIdValidator_BaseFactory(t || GridAreaIdValidator); };
GridAreaIdValidator.ɵdir = ɵɵdefineDirective({ type: GridAreaIdValidator, selectors: [["", "gridAreaId", "", "formControlName", ""], ["", "gridAreaId", "", "formControl", ""], ["", "gridAreaId", "", "ngModel", ""]], hostVars: 1, hostBindings: function GridAreaIdValidator_HostBindings(rf, ctx) { if (rf & 2) {
        ɵɵattribute("gridAreaId", ctx.gridAreaId ? ctx.gridAreaId : null);
    } }, inputs: { gridAreaId: "gridAreaId" }, features: [ɵɵProvidersFeature([{ provide: NG_VALIDATORS, useExisting: forwardRef(() => GridAreaIdValidator), multi: true }]), ɵɵInheritDefinitionFeature, ɵɵNgOnChangesFeature] });
const ɵGridAreaIdValidator_BaseFactory = ɵɵgetInheritedFactory(GridAreaIdValidator);
/*@__PURE__*/ (function () { ɵsetClassMetadata(GridAreaIdValidator, [{
        type: Directive,
        args: [{
                selector: "[gridAreaId][formControlName],[gridAreaId][formControl],[gridAreaId][ngModel]",
                providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => GridAreaIdValidator), multi: true }],
                host: { "[attr.gridAreaId]": "gridAreaId ? gridAreaId : null" },
            }]
    }], null, { gridAreaId: [{
            type: Input
        }] }); })();

class ValidatorsModule {
}
ValidatorsModule.ɵmod = ɵɵdefineNgModule({ type: ValidatorsModule });
ValidatorsModule.ɵinj = ɵɵdefineInjector({ factory: function ValidatorsModule_Factory(t) { return new (t || ValidatorsModule)(); }, imports: [[CommonModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵɵsetNgModuleScope(ValidatorsModule, { declarations: [EmailAddressValidator,
        GridAreaIdValidator,
        MaxValidator,
        MinValidator,
        OrganisationNumberValidator,
        PasswordValidator,
        PersonalNumberValidator,
        PhoneNumberValidator,
        PremiseIdValidator,
        PostalCodeValidator], imports: [CommonModule], exports: [EmailAddressValidator,
        GridAreaIdValidator,
        MaxValidator,
        MinValidator,
        OrganisationNumberValidator,
        PasswordValidator,
        PersonalNumberValidator,
        PhoneNumberValidator,
        PremiseIdValidator,
        PostalCodeValidator] }); })();
/*@__PURE__*/ (function () { ɵsetClassMetadata(ValidatorsModule, [{
        type: NgModule,
        args: [{
                declarations: [
                    EmailAddressValidator,
                    GridAreaIdValidator,
                    MaxValidator,
                    MinValidator,
                    OrganisationNumberValidator,
                    PasswordValidator,
                    PersonalNumberValidator,
                    PhoneNumberValidator,
                    PremiseIdValidator,
                    PostalCodeValidator,
                ],
                imports: [CommonModule],
                exports: [
                    EmailAddressValidator,
                    GridAreaIdValidator,
                    MaxValidator,
                    MinValidator,
                    OrganisationNumberValidator,
                    PasswordValidator,
                    PersonalNumberValidator,
                    PhoneNumberValidator,
                    PremiseIdValidator,
                    PostalCodeValidator,
                ],
            }]
    }], null, null); })();

/**
 * classes
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CookieInterceptor, DataLayerService, ELEMENT_ROUTES, ENVIRONMENT, Elements, ElementsModule, ElementsTestBed, ElementsValidators, EmailAddressValidator, Errors, GlobalStoreService, GridAreaIdValidator, HttpStatusCode, LegacyAngularCacheService, MaxValidator, MinValidator, MockupHttpInterceptor, MonthPipe, OrganisationNumberValidator, PasswordValidator, PersonalNumberValidator, PhoneNumberValidator, PickAllPipe, PipesModule, PostalCodeValidator, PremiseIdValidator, SlotDirective, SlotModule, TimeRangePipe, UnitPipe, ValidatorsModule, ViewState, app, cookieInterceptorProvider, createElementsClass, getElementsTestBed, httpResponseSimulation, resetElementsClass };
//# sourceMappingURL=vattenfall-core.js.map
